import React, { useState } from 'react';

const COLORS = {
  primary: '#C41E3A',
  primaryDark: '#9B1830',
  secondary: '#2D3748',
  accent: '#FFD700',
  success: '#38A169',
  warning: '#DD6B20',
  error: '#E53E3E',
  bg: '#F7FAFC',
  card: '#fff',
  border: '#E2E8F0',
  text: '#2D3748',
  textLight: '#718096',
  textWhite: '#fff',
};

const FONTS = {
  heading: "'Poppins', sans-serif",
  body: "'Inter', sans-serif",
  number: "'Montserrat', sans-serif",
};

const menuItems = [
  { key: 'dashboard', label: 'Dashboard', icon: '📊' },
  { key: 'students', label: 'Students', icon: '🎓' },
  { key: 'plans', label: 'Plans', icon: '📋' },
  { key: 'coaches', label: 'Coaches', icon: '🥋' },
  { key: 'belt-exams', label: 'Belt Exams', icon: '🏆' },
  { key: 'schedule', label: 'Schedule', icon: '📅' },
  { key: 'daily-sessions', label: 'Sessions', icon: '⏱️' },
  { key: 'leave-requests', label: 'Leave Requests', icon: '🗓️' },
  { key: 'exercises', label: 'Exercises', icon: '💪' },
  { key: 'commission', label: 'Commission', icon: '💰' },
  { key: 'payments', label: 'Payments', icon: '💳' },
  { key: 'finance', label: 'Finance', icon: '📈' },
  { key: 'whatsapp', label: 'WhatsApp', icon: '📱' },
  { key: 'attendance', label: 'Attendance', icon: '✅' },
  { key: 'reports', label: 'Reports', icon: '📋' },
  { key: 'settings', label: 'Settings', icon: '⚙️' },
];

const sampleStudents = [
  { id: 'STU-001', name: 'Fahad Al-Dosari', age: 14, discipline: 'Karate', coach: 'Yousif Salman', belt: 'Green', beltColor: '#38A169', status: 'Active', payment: 'Paid', attend: '92%' },
  { id: 'STU-002', name: 'Sara Ahmed', age: 22, discipline: 'Boxing', coach: 'Ahmad Khalil', belt: 'Blue', beltColor: '#3182CE', status: 'Active', payment: 'Overdue', attend: '88%' },
  { id: 'STU-003', name: 'Omar Hassan', age: 28, discipline: 'BJJ', coach: 'Mahmoud Rashid', belt: 'Purple', beltColor: '#805AD5', status: 'Active', payment: 'Paid', attend: '95%' },
  { id: 'STU-004', name: 'Layla Khalifa', age: 10, discipline: 'Karate', coach: 'Yousif Salman', belt: 'Yellow', beltColor: '#ECC94B', status: 'Trial', payment: 'Free', attend: '100%' },
  { id: 'STU-005', name: 'Ali Mansoor', age: 35, discipline: 'Kickboxing', coach: 'Ali Abdullah', belt: 'Red', beltColor: '#E53E3E', status: 'Inactive', payment: 'Overdue', attend: '64%' },
];

const sampleCoaches = [
  { name: 'Yousif Salman', discipline: 'Karate', belt: 'Black Belt 3rd Dan', exp: '12 years', students: 32, sessions: 18, earnings: '520 BHD', attend: '98%', status: 'Active', avatar: 'YS' },
  { name: 'Ahmad Khalil', discipline: 'Boxing', belt: 'Professional Trainer', exp: '8 years', students: 24, sessions: 16, earnings: '440 BHD', attend: '96%', status: 'Active', avatar: 'AK' },
  { name: 'Mahmoud Rashid', discipline: 'BJJ', belt: 'Black Belt 2nd Dan', exp: '10 years', students: 18, sessions: 14, earnings: '380 BHD', attend: '94%', status: 'Active', avatar: 'MR' },
  { name: 'Ali Abdullah', discipline: 'Kickboxing', belt: 'Black Belt 1st Dan', exp: '6 years', students: 20, sessions: 12, earnings: '360 BHD', attend: '0%', status: 'On Leave', avatar: 'AA' },
];

const sampleExercises = [
  { title: 'Front Kick Combo', discipline: 'Karate', level: 'Beginner', color: '#C41E3A', assigned: 24 },
  { title: 'Jab-Cross-Hook', discipline: 'Boxing', level: 'Intermediate', color: '#3182CE', assigned: 18 },
  { title: 'Triangle Choke', discipline: 'BJJ', level: 'Advanced', color: '#805AD5', assigned: 12 },
  { title: 'Roundhouse Kick', discipline: 'Kickboxing', level: 'Intermediate', color: '#DD6B20', assigned: 20 },
  { title: 'Kata Heian Shodan', discipline: 'Karate', level: 'Beginner', color: '#C41E3A', assigned: 30 },
  { title: 'Guard Pass Drill', discipline: 'BJJ', level: 'Beginner', color: '#805AD5', assigned: 15 },
];

/* ─── style helpers ─── */
const kpiCard = (bg = COLORS.card) => ({
  background: bg, borderRadius: 12, padding: '20px 24px', flex: 1,
  boxShadow: '0 1px 3px rgba(0,0,0,.08)', minWidth: 180,
});
const badge = (bg, color = '#fff') => ({
  display: 'inline-block', padding: '2px 10px', borderRadius: 20,
  fontSize: 12, fontFamily: FONTS.body, background: bg, color,
  fontWeight: 600,
});
const btn = (bg = COLORS.primary, color = '#fff') => ({
  padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer',
  fontFamily: FONTS.body, fontWeight: 600, fontSize: 13,
  background: bg, color,
});
const tableStyle = {
  width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: FONTS.body,
};
const th = {
  textAlign: 'left', padding: '10px 14px', borderBottom: `2px solid ${COLORS.border}`,
  fontWeight: 600, color: COLORS.textLight, fontSize: 12, textTransform: 'uppercase',
  letterSpacing: '.5px',
};
const td = {
  padding: '12px 14px', borderBottom: `1px solid ${COLORS.border}`, color: COLORS.text,
};
const inputStyle = {
  width: '100%', padding: '10px 14px', borderRadius: 8, border: `1px solid ${COLORS.border}`,
  fontFamily: FONTS.body, fontSize: 14, outline: 'none', boxSizing: 'border-box',
};
const selectStyle = { ...inputStyle, appearance: 'auto' };
const sectionTitle = {
  fontFamily: FONTS.heading, fontSize: 18, fontWeight: 700, color: COLORS.text, margin: '0 0 16px',
};
const cardWrap = {
  background: COLORS.card, borderRadius: 12, padding: 24,
  boxShadow: '0 1px 3px rgba(0,0,0,.08)', marginBottom: 20,
};

/* ─── bar chart helper ─── */
const BarChart = ({ data, maxVal, height = 160 }) => (
  <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, height, paddingTop: 8 }}>
    {data.map((d, i) => {
      const h = (d.value / maxVal) * (height - 30);
      return (
        <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
          <span style={{ fontSize: 11, fontFamily: FONTS.number, fontWeight: 700, marginBottom: 4, color: COLORS.text }}>{d.value}</span>
          <div style={{ width: '100%', maxWidth: 48, height: h, background: d.color || COLORS.primary, borderRadius: '6px 6px 0 0' }} />
          <span style={{ fontSize: 11, marginTop: 6, color: COLORS.textLight, fontFamily: FONTS.body }}>{d.label}</span>
        </div>
      );
    })}
  </div>
);

/* ═══════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════ */
export default function DojoAdminWireframes({ initialScreen = 'dashboard' }) {
  const [currentScreen, setCurrentScreen] = useState(initialScreen);
  const [viewMode, setViewMode] = useState('desktop');
  const [kioskMode, setKioskMode] = useState('qr'); // 'qr' or 'biometric'

  /* ─────────── Sidebar ─────────── */
  const Sidebar = () => (
    <div style={{
      width: 220, minWidth: 220, background: COLORS.secondary, color: COLORS.textWhite,
      display: 'flex', flexDirection: 'column', height: '100%', fontFamily: FONTS.body,
    }}>
      <div style={{ padding: '24px 20px 8px', borderBottom: '1px solid rgba(255,255,255,.1)' }}>
        <div style={{ fontSize: 22, fontFamily: FONTS.heading, fontWeight: 700, marginBottom: 2 }}>🥋 DojoMaster</div>
        <div style={{ fontSize: 10, opacity: .65, letterSpacing: '.5px' }}>Bahrain Self Defense Team</div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '12px 0' }}>
        {menuItems.map(m => {
          const active = currentScreen === m.key || (m.key === 'daily-sessions' && currentScreen === 'substitute') || (m.key === 'coaches' && currentScreen === 'add-coach');
          return (
            <div key={m.key} onClick={() => setCurrentScreen(m.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '10px 20px',
                cursor: 'pointer', fontSize: 13, fontWeight: active ? 600 : 400,
                borderLeft: active ? `3px solid ${COLORS.primary}` : '3px solid transparent',
                background: active ? 'rgba(196,30,58,.15)' : 'transparent',
                color: active ? COLORS.textWhite : 'rgba(255,255,255,.7)',
                transition: 'all .15s',
              }}>
              <span>{m.icon}</span><span>{m.label}</span>
            </div>
          );
        })}
      </div>
      <div style={{ padding: '14px 20px', borderTop: '1px solid rgba(255,255,255,.1)', fontSize: 11, opacity: .5 }}>v1.0 &middot; &copy; 2026</div>
    </div>
  );

  /* ─────────── Browser Frame ─────────── */
  const BrowserFrame = ({ children }) => (
    <div style={{ width: '100%', maxWidth: 1280, margin: '0 auto', borderRadius: 12, overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,.12)', border: `1px solid ${COLORS.border}`, background: COLORS.bg }}>
      {/* title bar */}
      <div style={{ background: '#E2E8F0', display: 'flex', alignItems: 'center', padding: '10px 16px', gap: 8 }}>
        <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#FC5753' }} />
        <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#FDBC40' }} />
        <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#33C748' }} />
        <div style={{ flex: 1, marginLeft: 12, background: '#fff', borderRadius: 6, padding: '6px 14px', fontSize: 12, color: COLORS.textLight, fontFamily: FONTS.body }}>
          https://admin.dojomaster.bh/{currentScreen}
        </div>
      </div>
      {/* body */}
      <div style={{ display: 'flex', height: 780, overflow: 'hidden' }}>
        <Sidebar />
        <div style={{ flex: 1, overflowY: 'auto', padding: 28, background: COLORS.bg }}>
          {children}
        </div>
      </div>
    </div>
  );

  /* ─────────── Tablet Sidebar (icons only) ─────────── */
  const TabletSidebar = () => (
    <div style={{
      width: 60, minWidth: 60, background: COLORS.secondary, color: COLORS.textWhite,
      display: 'flex', flexDirection: 'column', height: '100%', fontFamily: FONTS.body,
    }}>
      <div style={{ padding: '16px 0', textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,.1)', fontSize: 20 }}>🥋</div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
        {menuItems.map(m => {
          const active = currentScreen === m.key || (m.key === 'daily-sessions' && currentScreen === 'substitute') || (m.key === 'coaches' && currentScreen === 'add-coach');
          return (
            <div key={m.key} onClick={() => setCurrentScreen(m.key)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '12px 0',
                cursor: 'pointer', fontSize: 18,
                background: active ? 'rgba(196,30,58,.15)' : 'transparent',
                borderLeft: active ? `3px solid ${COLORS.primary}` : '3px solid transparent',
                transition: 'all .15s',
              }}
              title={m.label}>
              <span>{m.icon}</span>
            </div>
          );
        })}
      </div>
      <div style={{ padding: '10px 0', textAlign: 'center', borderTop: '1px solid rgba(255,255,255,.1)', fontSize: 9, opacity: .5 }}>v1.0</div>
    </div>
  );

  /* ─────────── Tablet Frame ─────────── */
  const TabletFrame = ({ children, noSidebar }) => (
    <div style={{ width: '100%', maxWidth: 1024, margin: '0 auto', borderRadius: 24, overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,.18)', border: '8px solid #1A1A2E', background: COLORS.bg }}>
      {/* tablet status bar */}
      <div style={{ background: '#1A1A2E', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 20px' }}>
        <span style={{ fontSize: 12, fontFamily: FONTS.number, fontWeight: 600, color: '#fff' }}>9:41</span>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,.7)', fontFamily: FONTS.body }}>WiFi</span>
          <svg width="18" height="10" viewBox="0 0 22 10"><rect x="0" y="1" width="18" height="8" rx="1.5" stroke="#fff" strokeWidth="1" fill="none"/><rect x="1.5" y="2.5" width="13" height="5" rx="0.5" fill="#38A169"/><rect x="19" y="3" width="2" height="4" rx="0.5" fill="#fff"/></svg>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,.7)', fontFamily: FONTS.number }}>87%</span>
        </div>
      </div>
      {/* body */}
      <div style={{ display: 'flex', height: 700, overflow: 'hidden' }}>
        {!noSidebar && <TabletSidebar />}
        <div style={{ flex: 1, overflowY: 'auto', padding: noSidebar ? 0 : 20, background: COLORS.bg }}>
          {children}
        </div>
      </div>
      {/* home indicator */}
      <div style={{ background: '#1A1A2E', padding: '6px 0 8px', display: 'flex', justifyContent: 'center' }}>
        <div style={{ width: 120, height: 4, borderRadius: 2, background: 'rgba(255,255,255,.4)' }} />
      </div>
    </div>
  );

  /* ─────────── PAGE HEADER ─────────── */
  const PageHeader = ({ title, subtitle, actions }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
      <div>
        <h1 style={{ fontFamily: FONTS.heading, fontSize: 26, fontWeight: 700, color: COLORS.text, margin: 0 }}>{title}</h1>
        {subtitle && <p style={{ margin: '4px 0 0', color: COLORS.textLight, fontSize: 14, fontFamily: FONTS.body }}>{subtitle}</p>}
      </div>
      {actions && <div style={{ display: 'flex', gap: 10 }}>{actions}</div>}
    </div>
  );

  /* ─────────── BREADCRUMB ─────────── */
  const Breadcrumb = ({ items }) => (
    <div style={{ display: 'flex', gap: 6, fontSize: 13, fontFamily: FONTS.body, color: COLORS.textLight, marginBottom: 18 }}>
      {items.map((item, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span>/</span>}
          {item.onClick
            ? <span onClick={item.onClick} style={{ color: COLORS.primary, cursor: 'pointer' }}>{item.label}</span>
            : <span style={{ color: COLORS.text, fontWeight: 600 }}>{item.label}</span>}
        </React.Fragment>
      ))}
    </div>
  );

  /* ═══════════════════════════════════════
     SCREEN 1 — DASHBOARD
     ═══════════════════════════════════════ */
  const renderDashboard = () => (
    <>
      <PageHeader title="Dashboard" subtitle="Welcome back, Admin — Wednesday, Feb 18 2026" />
      {/* KPIs */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          { label: 'Total Students', value: '142', icon: '🎓', change: '+8 this month', color: COLORS.primary },
          { label: 'Active Coaches', value: '24', icon: '🥋', change: '1 on leave', color: COLORS.warning },
          { label: 'Revenue (BHD)', value: '4,820', icon: '💰', change: '+12% vs Jan', color: COLORS.success },
          { label: 'Attendance Rate', value: '87%', icon: '✅', change: '+3% vs last week', color: '#3182CE' },
        ].map((k, i) => (
          <div key={i} style={kpiCard()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 13, color: COLORS.textLight, fontFamily: FONTS.body }}>{k.label}</span>
              <span style={{ fontSize: 22 }}>{k.icon}</span>
            </div>
            <div style={{ fontFamily: FONTS.number, fontSize: 32, fontWeight: 800, color: k.color }}>{k.value}</div>
            <div style={{ fontSize: 12, color: COLORS.textLight, marginTop: 4, fontFamily: FONTS.body }}>{k.change}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 20, marginBottom: 24 }}>
        {/* Today's Sessions */}
        <div style={{ ...cardWrap, flex: 2 }}>
          <h3 style={sectionTitle}>Today's Sessions</h3>
          <table style={tableStyle}>
            <thead><tr>
              <th style={th}>Session</th><th style={th}>Time</th><th style={th}>Coach</th><th style={th}>Students</th><th style={th}>Status</th>
            </tr></thead>
            <tbody>
              {[
                { name: 'Kids Karate', time: '8:00 AM', coach: 'Yousif Salman', students: '12/15', status: 'Done', statusColor: COLORS.success },
                { name: 'Adult Boxing', time: '5:00 PM', coach: 'Ahmad Khalil', students: '8/20', status: 'LIVE', statusColor: COLORS.primary, qrActive: true },
                { name: 'BJJ Advanced', time: '7:00 PM', coach: 'Mahmoud Rashid', students: '—', status: 'Upcoming', statusColor: '#3182CE' },
                { name: 'Kickboxing', time: '7:00 PM', coach: 'Ali Abdullah', students: '—', status: 'Needs Sub', statusColor: COLORS.error },
              ].map((s, i) => (
                <tr key={i}>
                  <td style={td}>{s.name}</td>
                  <td style={td}>{s.time}</td>
                  <td style={td}>{s.coach}</td>
                  <td style={{ ...td, fontFamily: FONTS.number }}>{s.students}</td>
                  <td style={td}>
                    <span style={badge(s.statusColor)}>{s.status}</span>
                    {s.qrActive && <span style={{ ...badge('#3182CE'), marginLeft: 6, fontSize: 10 }}>📱 QR Active</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Quick Actions */}
        <div style={{ ...cardWrap, flex: 1 }}>
          <h3 style={sectionTitle}>Quick Actions</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              { label: 'Start Session', icon: '▶️', bg: '#3182CE' },
              { label: 'Add Student', icon: '➕', bg: COLORS.primary },
              { label: 'Record Payment', icon: '💳', bg: COLORS.success },
              { label: 'Assign Substitute', icon: '🔄', bg: COLORS.warning },
              { label: 'Send WhatsApp', icon: '📱', bg: '#25D366' },
              { label: 'Generate Report', icon: '📋', bg: '#805AD5' },
            ].map((a, i) => (
              <button key={i} onClick={() => { if (a.label === 'Add Student') setCurrentScreen('add-student'); if (a.label === 'Assign Substitute') setCurrentScreen('substitute'); if (a.label === 'Start Session') setCurrentScreen('session-kiosk'); }}
                style={{ ...btn(a.bg), display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-start', width: '100%', padding: '12px 16px' }}>
                <span>{a.icon}</span>{a.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Active Kiosk Card */}
      <div style={{ ...cardWrap, marginBottom: 24, borderLeft: `5px solid #3182CE`, display: 'flex', alignItems: 'center', gap: 20 }}>
        {/* Mini QR */}
        <div style={{ width: 64, height: 64, background: '#fff', borderRadius: 8, border: `1px solid ${COLORS.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 2, width: 40, height: 40 }}>
            {Array.from({ length: 25 }).map((_, i) => (
              <div key={i} style={{ background: Math.random() > 0.4 ? COLORS.secondary : '#fff', borderRadius: 1 }} />
            ))}
          </div>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontFamily: FONTS.heading, fontSize: 15, fontWeight: 700, color: COLORS.text }}>Active Kiosk: Adult Boxing</div>
          <div style={{ fontSize: 13, color: COLORS.textLight, marginTop: 2 }}>
            <span style={{ fontFamily: FONTS.number, fontWeight: 700, color: COLORS.primary }}>12</span> / 20 checked in &middot; QR Mode &middot; Coach Ahmad Khalil
          </div>
        </div>
        <button onClick={() => setCurrentScreen('session-kiosk')}
          style={{ ...btn('#3182CE'), padding: '10px 20px', whiteSpace: 'nowrap' }}>Open Kiosk</button>
      </div>

      <div style={{ display: 'flex', gap: 20, marginBottom: 24 }}>
        {/* Alerts */}
        <div style={{ ...cardWrap, flex: 1 }}>
          <h3 style={sectionTitle}>Alerts &amp; Notifications</h3>
          {[
            { text: '12 overdue payments need follow-up', color: COLORS.error, icon: '🔴' },
            { text: '5 memberships expiring this week', color: COLORS.warning, icon: '🟡' },
            { text: 'Coach Ali Abdullah on leave — Kickboxing needs substitute', color: COLORS.warning, icon: '🟠' },
            { text: '3 students ready for belt exam (200+ hrs)', color: COLORS.success, icon: '🟢' },
          ].map((a, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: i < 3 ? `1px solid ${COLORS.border}` : 'none' }}>
              <span>{a.icon}</span>
              <span style={{ fontSize: 13, color: COLORS.text, fontFamily: FONTS.body }}>{a.text}</span>
            </div>
          ))}
        </div>

        {/* Revenue Chart */}
        <div style={{ ...cardWrap, flex: 1 }}>
          <h3 style={sectionTitle}>Revenue (6 Months)</h3>
          <BarChart
            data={[
              { label: 'Sep', value: 3200, color: COLORS.primary },
              { label: 'Oct', value: 3650, color: COLORS.primary },
              { label: 'Nov', value: 3900, color: COLORS.primary },
              { label: 'Dec', value: 4100, color: COLORS.primary },
              { label: 'Jan', value: 4300, color: COLORS.primary },
              { label: 'Feb', value: 4820, color: COLORS.accent },
            ]}
            maxVal={5500}
          />
        </div>
      </div>

      {/* Recent Activity */}
      <div style={cardWrap}>
        <h3 style={sectionTitle}>Recent Activity</h3>
        {[
          { time: '2 min ago', text: 'Sara Ahmed marked present for Adult Boxing', icon: '✅' },
          { time: '15 min ago', text: 'Payment received: Omar Hassan — 55 BHD (Benefit Pay)', icon: '💳' },
          { time: '1 hr ago', text: 'New student registered: Noora Al-Khalifa (Trial)', icon: '🎓' },
          { time: '2 hrs ago', text: 'Coach Yousif completed Kids Karate session (12/15 attended)', icon: '🥋' },
          { time: '3 hrs ago', text: 'WhatsApp reminder sent to 8 students with overdue payments', icon: '📱' },
        ].map((a, i) => (
          <div key={i} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: i < 4 ? `1px solid ${COLORS.border}` : 'none', alignItems: 'center' }}>
            <span style={{ fontSize: 18 }}>{a.icon}</span>
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: 13, color: COLORS.text, fontFamily: FONTS.body }}>{a.text}</span>
            </div>
            <span style={{ fontSize: 11, color: COLORS.textLight, fontFamily: FONTS.body, whiteSpace: 'nowrap' }}>{a.time}</span>
          </div>
        ))}
      </div>
    </>
  );

  /* ═══════════════════════════════════════
     SCREEN 2 — STUDENTS
     ═══════════════════════════════════════ */
  const renderStudents = () => (
    <>
      <PageHeader title="Students" subtitle="142 total students"
        actions={<>
          <button style={btn('#fff', COLORS.text)} onClick={() => {}}>Export</button>
          <button style={btn(COLORS.warning)}>Bulk Remind</button>
          <button style={btn()} onClick={() => setCurrentScreen('add-student')}>+ Add Student</button>
        </>}
      />
      {/* Filters */}
      <div style={{ ...cardWrap, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <input placeholder="Search by name or ID..." style={{ ...inputStyle, maxWidth: 260 }} />
        <select style={{ ...selectStyle, maxWidth: 150 }}><option>All Disciplines</option><option>Karate</option><option>Boxing</option><option>BJJ</option><option>Kickboxing</option></select>
        <select style={{ ...selectStyle, maxWidth: 150 }}><option>All Coaches</option><option>Yousif Salman</option><option>Ahmad Khalil</option><option>Mahmoud Rashid</option><option>Ali Abdullah</option></select>
        <select style={{ ...selectStyle, maxWidth: 130 }}><option>All Status</option><option>Active</option><option>Trial</option><option>Inactive</option></select>
        <select style={{ ...selectStyle, maxWidth: 150 }}><option>Payment Status</option><option>Paid</option><option>Overdue</option><option>Free</option></select>
      </div>

      {/* Table */}
      <div style={cardWrap}>
        <table style={tableStyle}>
          <thead><tr>
            <th style={th}>Student</th><th style={th}>Age</th><th style={th}>Discipline</th><th style={th}>Coach</th>
            <th style={th}>Belt</th><th style={th}>Status</th><th style={th}>Payment</th><th style={th}>Attend%</th><th style={th}>Actions</th>
          </tr></thead>
          <tbody>
            {sampleStudents.map((s, i) => (
              <tr key={i} style={{ cursor: 'pointer' }} onClick={() => setCurrentScreen('student-profile')}>
                <td style={{ ...td, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ width: 34, height: 34, borderRadius: '50%', background: COLORS.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700, fontFamily: FONTS.body }}>
                    {s.name.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{s.name}</div>
                    <div style={{ fontSize: 11, color: COLORS.textLight }}>{s.id}</div>
                  </div>
                </td>
                <td style={{ ...td, fontFamily: FONTS.number }}>{s.age}</td>
                <td style={td}>{s.discipline}</td>
                <td style={td}>{s.coach}</td>
                <td style={td}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ width: 30, height: 6, borderRadius: 3, background: s.beltColor }} />
                    <span style={{ fontSize: 12 }}>{s.belt}</span>
                  </div>
                </td>
                <td style={td}><span style={badge(s.status === 'Active' ? COLORS.success : s.status === 'Trial' ? '#3182CE' : COLORS.textLight)}>{s.status}</span></td>
                <td style={td}><span style={badge(s.payment === 'Paid' ? COLORS.success : s.payment === 'Overdue' ? COLORS.error : '#3182CE')}>{s.payment}</span></td>
                <td style={{ ...td, fontFamily: FONTS.number }}>{s.attend}</td>
                <td style={td}>
                  <button style={{ ...btn(COLORS.bg, COLORS.primary), padding: '4px 10px', fontSize: 11, border: `1px solid ${COLORS.primary}` }}>View</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* Pagination */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, fontSize: 13, color: COLORS.textLight, fontFamily: FONTS.body }}>
          <span>Showing 1-5 of 142 students</span>
          <div style={{ display: 'flex', gap: 4 }}>
            {[1, 2, 3, '...', 29].map((p, i) => (
              <button key={i} style={{ ...btn(p === 1 ? COLORS.primary : '#fff', p === 1 ? '#fff' : COLORS.text), padding: '6px 12px', fontSize: 12, border: `1px solid ${COLORS.border}` }}>{p}</button>
            ))}
          </div>
        </div>
      </div>
    </>
  );

  /* ═══════════════════════════════════════
     SCREEN 3 — STUDENT PROFILE
     ═══════════════════════════════════════ */
  const renderStudentProfile = () => (
    <>
      <Breadcrumb items={[
        { label: 'Students', onClick: () => setCurrentScreen('students') },
        { label: 'Fahad Al-Dosari' },
      ]} />
      {/* Hero */}
      <div style={{ background: COLORS.secondary, borderRadius: 12, padding: 28, color: '#fff', marginBottom: 24, display: 'flex', gap: 28, alignItems: 'center' }}>
        <div style={{ width: 80, height: 80, borderRadius: '50%', background: COLORS.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 700, fontFamily: FONTS.heading }}>FA</div>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontFamily: FONTS.heading, fontSize: 24, fontWeight: 700, margin: 0 }}>Fahad Al-Dosari</h2>
          <div style={{ fontSize: 13, opacity: .7, marginTop: 4 }}>STU-001 &middot; Age 14 &middot; Karate &middot; Coach Yousif Salman</div>
          <div style={{ display: 'flex', gap: 20, marginTop: 14 }}>
            {[
              { label: 'Belt', value: 'Green', vColor: '#38A169' },
              { label: 'Hours', value: '186', vColor: COLORS.accent },
              { label: 'Next Exam', value: '200 hrs', vColor: '#fff' },
              { label: 'Attendance', value: '92%', vColor: '#fff' },
            ].map((s, i) => (
              <div key={i}>
                <div style={{ fontSize: 10, opacity: .6, textTransform: 'uppercase', letterSpacing: '.5px' }}>{s.label}</div>
                <div style={{ fontFamily: FONTS.number, fontSize: 18, fontWeight: 700, color: s.vColor, marginTop: 2 }}>{s.value}</div>
              </div>
            ))}
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
          <button style={btn(COLORS.primary)}>Edit Profile</button>
          <div style={{ background: COLORS.accent, color: '#744210', borderRadius: 10, padding: '8px 14px', textAlign: 'center', fontFamily: FONTS.body, fontSize: 12, fontWeight: 700 }}>
            🏆 Exam Eligible! <div style={{ fontWeight: 400, fontSize: 10, marginTop: 2 }}>200/200 hrs — ready for Blue Belt</div>
          </div>
          <button style={{ ...btn(COLORS.success), fontSize: 12 }} onClick={() => setCurrentScreen('belt-exams')}>Schedule Exam →</button>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 20, marginBottom: 24 }}>
        {/* Parent Info */}
        <div style={{ ...cardWrap, flex: 1 }}>
          <h3 style={sectionTitle}>Guardian Information</h3>
          <div style={{ fontSize: 13, fontFamily: FONTS.body, color: COLORS.text }}>
            <div style={{ marginBottom: 8 }}><strong>Name:</strong> Mohammed Al-Dosari</div>
            <div style={{ marginBottom: 8 }}><strong>Phone:</strong> +973 3600 1234</div>
            <div style={{ marginBottom: 8 }}><strong>Email:</strong> mohammed.dosari@email.com</div>
            <div><strong>Relation:</strong> Father</div>
          </div>
        </div>
        {/* Stats Grid */}
        <div style={{ ...cardWrap, flex: 2 }}>
          <h3 style={sectionTitle}>Performance Stats</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 }}>
            {[
              { label: 'Total Sessions', value: '124', color: COLORS.primary },
              { label: 'Attended', value: '114', color: COLORS.success },
              { label: 'Missed', value: '10', color: COLORS.error },
              { label: 'Training Hours', value: '186', color: '#3182CE' },
              { label: 'Exercises Done', value: '48', color: COLORS.warning },
              { label: 'Belt Exams Passed', value: '3', color: COLORS.accent },
            ].map((s, i) => (
              <div key={i} style={{ background: COLORS.bg, borderRadius: 8, padding: 14, textAlign: 'center' }}>
                <div style={{ fontFamily: FONTS.number, fontSize: 24, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 11, color: COLORS.textLight, marginTop: 4 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs placeholder */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20 }}>
        {['Progress', 'Attendance', 'Exercises', 'Payments', 'Notes'].map((t, i) => (
          <div key={i} style={{
            padding: '10px 22px', fontSize: 13, fontWeight: i === 0 ? 700 : 400, fontFamily: FONTS.body,
            cursor: 'pointer', borderBottom: i === 0 ? `3px solid ${COLORS.primary}` : `3px solid transparent`,
            color: i === 0 ? COLORS.primary : COLORS.textLight,
          }}>{t}</div>
        ))}
      </div>

      {/* Belt Progression */}
      <div style={cardWrap}>
        <h3 style={sectionTitle}>Belt Progression Timeline</h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
          {[
            { belt: 'White', color: '#E2E8F0', date: 'Jan 2023', hrs: '0' },
            { belt: 'Yellow', color: '#ECC94B', date: 'Jun 2023', hrs: '50' },
            { belt: 'Orange', color: '#DD6B20', date: 'Jan 2024', hrs: '100' },
            { belt: 'Green', color: '#38A169', date: 'Sep 2024', hrs: '150', current: true },
            { belt: 'Blue', color: '#3182CE', date: '—', hrs: '200', future: true },
          ].map((b, i) => (
            <React.Fragment key={i}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', opacity: b.future ? .4 : 1 }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: b.color, border: b.current ? `3px solid ${COLORS.primary}` : '3px solid transparent', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {b.current && <span style={{ fontSize: 14 }}>✓</span>}
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, marginTop: 6, fontFamily: FONTS.body, color: COLORS.text }}>{b.belt}</div>
                <div style={{ fontSize: 10, color: COLORS.textLight }}>{b.date}</div>
                <div style={{ fontSize: 10, color: COLORS.textLight }}>{b.hrs} hrs</div>
              </div>
              {i < 4 && <div style={{ flex: 1, height: 3, background: b.future ? COLORS.border : b.color, minWidth: 40 }} />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* Assigned Exercises */}
      <div style={cardWrap}>
        <h3 style={sectionTitle}>Assigned Exercises</h3>
        <table style={tableStyle}>
          <thead><tr><th style={th}>Exercise</th><th style={th}>Type</th><th style={th}>Sets / Reps</th><th style={th}>Status</th><th style={th}>Video</th></tr></thead>
          <tbody>
            {[
              { name: 'Front Kick Combo', type: 'Kata', sets: '3 x 10', status: 'Completed', color: COLORS.success },
              { name: 'Kata Heian Shodan', type: 'Kata', sets: '5 reps', status: 'Completed', color: COLORS.success },
              { name: 'Sparring Footwork', type: 'Kumite', sets: '4 x 3 min', status: 'In Progress', color: COLORS.warning },
              { name: 'Board Break Practice', type: 'Strength', sets: '2 x 5', status: 'Not Started', color: COLORS.textLight },
            ].map((e, i) => (
              <tr key={i}>
                <td style={{ ...td, fontWeight: 600 }}>{e.name}</td>
                <td style={td}>{e.type}</td>
                <td style={{ ...td, fontFamily: FONTS.number }}>{e.sets}</td>
                <td style={td}><span style={badge(e.color)}>{e.status}</span></td>
                <td style={td}><span style={{ color: COLORS.primary, cursor: 'pointer', fontSize: 12 }}>▶ Watch</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );

  /* ═══════════════════════════════════════
     SCREEN 4 — COACHES
     ═══════════════════════════════════════ */
  const renderCoaches = () => (
    <>
      <PageHeader title="Coaches" subtitle="24 coaches — 1 on leave"
        actions={<button style={btn()} onClick={() => setCurrentScreen('add-coach')}>+ Add Coach</button>}
      />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 20 }}>
        {sampleCoaches.map((c, i) => (
          <div key={i} style={{ ...cardWrap, cursor: 'pointer', position: 'relative' }}
            onClick={() => setCurrentScreen('coach-detail')}>
            {c.status === 'On Leave' && (
              <div style={{ position: 'absolute', top: 16, right: 16 }}>
                <span style={badge(COLORS.error)}>On Leave</span>
              </div>
            )}
            <div style={{ display: 'flex', gap: 18, alignItems: 'center', marginBottom: 18 }}>
              <div style={{ width: 60, height: 60, borderRadius: '50%', background: c.status === 'On Leave' ? COLORS.textLight : COLORS.primary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, fontWeight: 700, fontFamily: FONTS.heading }}>{c.avatar}</div>
              <div>
                <div style={{ fontFamily: FONTS.heading, fontSize: 18, fontWeight: 700, color: COLORS.text }}>{c.name}</div>
                <div style={{ fontSize: 13, color: COLORS.textLight }}>{c.belt}</div>
                <div style={{ fontSize: 12, color: COLORS.textLight }}>{c.discipline} &middot; {c.exp}</div>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
              {[
                { label: 'Students', value: c.students },
                { label: 'Sessions/mo', value: c.sessions },
                { label: 'Earnings', value: c.earnings },
                { label: 'Attend %', value: c.attend },
              ].map((s, j) => (
                <div key={j} style={{ textAlign: 'center', background: COLORS.bg, borderRadius: 8, padding: '10px 4px' }}>
                  <div style={{ fontFamily: FONTS.number, fontSize: 16, fontWeight: 700, color: COLORS.text }}>{s.value}</div>
                  <div style={{ fontSize: 10, color: COLORS.textLight, marginTop: 2 }}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </>
  );

  /* ═══════════════════════════════════════
     SCREEN 5 — COACH DETAIL
     ═══════════════════════════════════════ */
  const renderCoachDetail = () => (
    <>
      <Breadcrumb items={[
        { label: 'Coaches', onClick: () => setCurrentScreen('coaches') },
        { label: 'Yousif Salman' },
      ]} />
      {/* Hero */}
      <div style={{ background: COLORS.secondary, borderRadius: 12, padding: 28, color: '#fff', marginBottom: 24, display: 'flex', gap: 28, alignItems: 'center' }}>
        <div style={{ width: 80, height: 80, borderRadius: '50%', background: COLORS.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 700, fontFamily: FONTS.heading }}>YS</div>
        <div style={{ flex: 1 }}>
          <h2 style={{ fontFamily: FONTS.heading, fontSize: 24, fontWeight: 700, margin: 0 }}>Yousif Salman</h2>
          <div style={{ fontSize: 13, opacity: .7, marginTop: 4 }}>Black Belt 3rd Dan &middot; Karate &middot; 12 years experience</div>
          <div style={{ display: 'flex', gap: 24, marginTop: 14 }}>
            {[
              { label: 'Students', value: '32' },
              { label: 'Sessions/mo', value: '18' },
              { label: 'Monthly Earnings', value: '520 BHD' },
              { label: 'Attendance', value: '98%' },
            ].map((s, i) => (
              <div key={i}>
                <div style={{ fontSize: 10, opacity: .6, textTransform: 'uppercase' }}>{s.label}</div>
                <div style={{ fontFamily: FONTS.number, fontSize: 18, fontWeight: 700, marginTop: 2 }}>{s.value}</div>
              </div>
            ))}
          </div>
        </div>
        <button style={btn(COLORS.primary)}>Edit Profile</button>
      </div>

      <div style={{ display: 'flex', gap: 20, marginBottom: 24 }}>
        {/* Commission Structure */}
        <div style={{ ...cardWrap, flex: 1 }}>
          <h3 style={sectionTitle}>Commission Structure</h3>
          <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
            {['Per Student', 'Per Session', 'Fixed Monthly'].map((t, i) => (
              <div key={i} style={{
                padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600, fontFamily: FONTS.body,
                background: i === 0 ? COLORS.primary : COLORS.bg,
                color: i === 0 ? '#fff' : COLORS.textLight,
                cursor: 'pointer',
              }}>{t}</div>
            ))}
          </div>
          <div style={{ background: COLORS.bg, borderRadius: 8, padding: 16 }}>
            <div style={{ fontSize: 13, fontFamily: FONTS.body, marginBottom: 8 }}>
              <strong>Formula:</strong> Per Student &times; Active Students
            </div>
            <div style={{ fontSize: 13, fontFamily: FONTS.body, marginBottom: 8 }}>
              <strong>Rate:</strong> <span style={{ fontFamily: FONTS.number, fontWeight: 700 }}>1.500 BHD</span> per student per session
            </div>
            <div style={{ fontSize: 13, fontFamily: FONTS.body }}>
              <strong>Feb 2026 Calculation:</strong>
            </div>
            <div style={{ fontFamily: FONTS.number, fontSize: 14, marginTop: 8, padding: 12, background: '#fff', borderRadius: 6 }}>
              18 sessions &times; avg 28.8 students &times; 1.000 BHD = <strong style={{ color: COLORS.primary }}>520.000 BHD</strong>
            </div>
          </div>
        </div>

        {/* Recent Sessions */}
        <div style={{ ...cardWrap, flex: 1 }}>
          <h3 style={sectionTitle}>Recent Sessions</h3>
          <table style={tableStyle}>
            <thead><tr><th style={th}>Date</th><th style={th}>Session</th><th style={th}>Attended</th><th style={th}>Status</th></tr></thead>
            <tbody>
              {[
                { date: 'Feb 18', session: 'Kids Karate', attended: '12/15', status: 'Completed' },
                { date: 'Feb 17', session: 'Kids Karate', attended: '14/15', status: 'Completed' },
                { date: 'Feb 17', session: 'Advanced Kata', attended: '8/10', status: 'Completed' },
                { date: 'Feb 16', session: 'Kids Karate', attended: '13/15', status: 'Completed' },
                { date: 'Feb 15', session: 'Advanced Kata', attended: '9/10', status: 'Completed' },
              ].map((s, i) => (
                <tr key={i}>
                  <td style={{ ...td, fontFamily: FONTS.number }}>{s.date}</td>
                  <td style={td}>{s.session}</td>
                  <td style={{ ...td, fontFamily: FONTS.number }}>{s.attended}</td>
                  <td style={td}><span style={badge(COLORS.success)}>{s.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  /* ═══════════════════════════════════════
     SCREEN 5b — ADD COACH
     ═══════════════════════════════════════ */
  const renderAddCoach = () => (
    <>
      <Breadcrumb items={[
        { label: 'Coaches', onClick: () => setCurrentScreen('coaches') },
        { label: 'Add New Coach' },
      ]} />
      <PageHeader title="Add New Coach" subtitle="Fill in all required fields to register a new coach" />
      <div style={{ display: 'flex', gap: 20 }}>
        <div style={{ ...cardWrap, flex: 2 }}>
          {/* Photo Upload */}
          <div style={{ display: 'flex', gap: 20, marginBottom: 24 }}>
            <div style={{
              width: 120, height: 120, borderRadius: 12, border: `2px dashed ${COLORS.border}`,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', background: COLORS.bg,
            }}>
              <span style={{ fontSize: 28, marginBottom: 4 }}>📷</span>
              <span style={{ fontSize: 11, color: COLORS.textLight, fontFamily: FONTS.body }}>Upload Photo</span>
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ ...sectionTitle, marginBottom: 12 }}>Personal Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ fontSize: 12, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 4 }}>Full Name *</label>
                  <input style={inputStyle} placeholder="Enter full name" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 4 }}>Phone *</label>
                  <input style={inputStyle} placeholder="+973 XXXX XXXX" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 4 }}>Email</label>
                  <input style={inputStyle} placeholder="coach@email.com" type="email" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 4 }}>Date of Birth</label>
                  <input style={inputStyle} type="date" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 4 }}>Gender</label>
                  <select style={selectStyle}><option>Select gender</option><option>Male</option><option>Female</option></select>
                </div>
              </div>
            </div>
          </div>

          {/* Professional Details */}
          <h3 style={{ ...sectionTitle, marginBottom: 12 }}>Professional Details</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 24 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 4 }}>Discipline *</label>
              <select style={selectStyle}><option>Select discipline</option><option>Karate</option><option>Boxing</option><option>BJJ</option><option>Kickboxing</option><option>Muay Thai</option></select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 4 }}>Belt / Qualification *</label>
              <input style={inputStyle} placeholder="e.g. Black Belt 3rd Dan" />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 4 }}>Years of Experience</label>
              <input style={inputStyle} type="number" placeholder="e.g. 8" min="0" />
            </div>
            <div />
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ fontSize: 12, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 4 }}>Bio / Notes</label>
              <textarea style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} placeholder="Brief background, certifications, teaching style..." />
            </div>
          </div>

          {/* Commission Structure */}
          <div style={{ ...cardWrap, background: COLORS.bg, marginBottom: 24 }}>
            <h3 style={{ ...sectionTitle, marginBottom: 12 }}>Commission Structure</h3>

            {/* Formula type pills */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              {['Per Student', 'Per Session', 'Fixed Monthly', 'Hybrid'].map((t, i) => (
                <div key={i} style={{
                  padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600, fontFamily: FONTS.body,
                  background: i === 0 ? COLORS.primary : COLORS.card,
                  color: i === 0 ? '#fff' : COLORS.textLight,
                  cursor: 'pointer', border: `1px solid ${i === 0 ? COLORS.primary : COLORS.border}`,
                  flexShrink: 0,
                }}>{t}{i === 3 && <span style={{ fontSize: 10, marginLeft: 4, opacity: .7 }}>Base+%</span>}</div>
              ))}
            </div>

            {/* Rate + Percentage row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 4 }}>Flat Rate (BHD) *</label>
                <input style={inputStyle} placeholder="e.g. 1.500 BHD" />
                <div style={{ fontSize: 11, color: COLORS.textLight, marginTop: 4, fontFamily: FONTS.body }}>Fixed amount per student / session</div>
              </div>
              <div>
                <label style={{ fontSize: 12, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 4 }}>Commission % <span style={{ fontWeight: 400, color: COLORS.textLight }}>(optional)</span></label>
                <div style={{ position: 'relative' }}>
                  <input style={{ ...inputStyle, paddingRight: 32 }} placeholder="e.g. 15" type="number" min="0" max="100" step="0.5" />
                  <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 14, color: COLORS.textLight, fontFamily: FONTS.number, fontWeight: 700, pointerEvents: 'none' }}>%</span>
                </div>
                <div style={{ fontSize: 11, color: COLORS.textLight, marginTop: 4, fontFamily: FONTS.body }}>% of session revenue collected</div>
              </div>
            </div>

            {/* Live formula preview */}
            <div style={{ background: COLORS.card, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: COLORS.textLight, fontFamily: FONTS.body, marginBottom: 10, textTransform: 'uppercase', letterSpacing: '.4px' }}>Formula Preview</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontFamily: FONTS.body }}>
                  <span style={{ color: COLORS.textLight }}>Flat rate &times; students per session</span>
                  <span style={{ fontFamily: FONTS.number, fontWeight: 700 }}>1.500 &times; 20 = <span style={{ color: COLORS.primary }}>30.000 BHD</span></span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontFamily: FONTS.body }}>
                  <span style={{ color: COLORS.textLight }}>Commission % of session revenue</span>
                  <span style={{ fontFamily: FONTS.number, fontWeight: 700 }}>15% &times; 200 = <span style={{ color: COLORS.primary }}>30.000 BHD</span></span>
                </div>
                <div style={{ borderTop: `1px dashed ${COLORS.border}`, paddingTop: 8, marginTop: 4, display: 'flex', justifyContent: 'space-between', fontSize: 13, fontFamily: FONTS.body, fontWeight: 700 }}>
                  <span>Estimated monthly earnings <span style={{ fontWeight: 400, color: COLORS.textLight }}>(18 sessions)</span></span>
                  <span style={{ fontFamily: FONTS.number, color: COLORS.success, fontSize: 15 }}>≈ 540.000 BHD</span>
                </div>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 12 }}>
            <button style={btn()}>Save Coach</button>
            <button style={btn(COLORS.bg, COLORS.text)} onClick={() => setCurrentScreen('coaches')}>Cancel</button>
          </div>
        </div>

        {/* Right Sidebar */}
        <div style={{ flex: 1 }}>
          <div style={cardWrap}>
            <h3 style={sectionTitle}>Profile Preview</h3>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '20px 0', background: COLORS.bg, borderRadius: 8 }}>
              <div style={{ width: 72, height: 72, borderRadius: '50%', background: COLORS.border, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, marginBottom: 10 }}>🥋</div>
              <div style={{ fontSize: 13, color: COLORS.textLight, fontFamily: FONTS.body, textAlign: 'center' }}>Coach name will appear here</div>
              <div style={{ fontSize: 11, color: COLORS.border, fontFamily: FONTS.body, marginTop: 4 }}>Discipline &middot; Belt</div>
            </div>
          </div>
          <div style={cardWrap}>
            <h3 style={sectionTitle}>Quick Tips</h3>
            {[
              "A system login will be created automatically with the coach's phone number",
              "Commission can be changed at any time from the coach's profile",
              'Coach will receive a WhatsApp welcome message after registration',
              'Assign sessions from the Schedule screen after saving',
            ].map((tip, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, padding: '8px 0', fontSize: 12, fontFamily: FONTS.body, color: COLORS.text, borderBottom: i < 3 ? `1px solid ${COLORS.border}` : 'none' }}>
                <span style={{ color: COLORS.primary }}>•</span>{tip}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );

  /* ═══════════════════════════════════════
     SCREEN 6 — COMMISSION
     ═══════════════════════════════════════ */
  const renderCommission = () => (
    <>
      <PageHeader title="Commission Processing" subtitle="February 2026 — Monthly Payroll"
        actions={<button style={btn(COLORS.success)}>Approve All &amp; Process</button>}
      />
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          { label: 'Total Commission', value: '3,840 BHD', icon: '💰', color: COLORS.primary },
          { label: 'Total Sessions', value: '186', icon: '⏱️', color: '#3182CE' },
          { label: 'Total Attendance', value: '2,480', icon: '✅', color: COLORS.success },
        ].map((k, i) => (
          <div key={i} style={kpiCard()}>
            <div style={{ fontSize: 13, color: COLORS.textLight, fontFamily: FONTS.body, marginBottom: 6 }}>{k.icon} {k.label}</div>
            <div style={{ fontFamily: FONTS.number, fontSize: 28, fontWeight: 800, color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      <div style={cardWrap}>
        <table style={tableStyle}>
          <thead><tr>
            <th style={th}>Coach</th><th style={th}>Formula</th><th style={th}>Sessions</th><th style={th}>Avg Students</th>
            <th style={th}>Net Payable</th><th style={th}>Status</th><th style={th}>Action</th>
          </tr></thead>
          <tbody>
            {[
              { name: 'Yousif Salman', formula: 'Per Student (1.000 BHD)', sessions: 18, avg: 28.8, net: '520.000', status: 'Pending' },
              { name: 'Ahmad Khalil', formula: 'Per Session (25.000 BHD)', sessions: 16, avg: 22.5, net: '400.000', status: 'Approved' },
              { name: 'Mahmoud Rashid', formula: 'Fixed (350.000 BHD)', sessions: 14, avg: 16.2, net: '350.000', status: 'Paid' },
            ].map((c, i) => (
              <tr key={i}>
                <td style={{ ...td, fontWeight: 600 }}>{c.name}</td>
                <td style={{ ...td, fontSize: 12 }}>{c.formula}</td>
                <td style={{ ...td, fontFamily: FONTS.number }}>{c.sessions}</td>
                <td style={{ ...td, fontFamily: FONTS.number }}>{c.avg}</td>
                <td style={{ ...td, fontFamily: FONTS.number, fontWeight: 700, color: COLORS.primary }}>{c.net} BHD</td>
                <td style={td}>
                  <span style={badge(c.status === 'Paid' ? COLORS.success : c.status === 'Approved' ? '#3182CE' : COLORS.warning)}>
                    {c.status}
                  </span>
                </td>
                <td style={td}>
                  {c.status === 'Pending' && <button style={{ ...btn(COLORS.success), padding: '4px 12px', fontSize: 11 }}>Approve</button>}
                  {c.status === 'Approved' && <button style={{ ...btn(COLORS.primary), padding: '4px 12px', fontSize: 11 }}>Mark Paid</button>}
                  {c.status === 'Paid' && <span style={{ fontSize: 12, color: COLORS.success }}>✓ Done</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );

  /* ═══════════════════════════════════════
     SCREEN 7 — SCHEDULE
     ═══════════════════════════════════════ */
  const renderSchedule = () => {
    const days = ['Saturday', 'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday'];
    const times = ['6:00 AM', '8:00 AM', '5:00 PM', '7:00 PM'];
    const sessionMap = {
      '6:00 AM-Saturday': { name: 'Open Gym', color: '#CBD5E0' },
      '6:00 AM-Monday': { name: 'Open Gym', color: '#CBD5E0' },
      '6:00 AM-Wednesday': { name: 'Open Gym', color: '#CBD5E0' },
      '8:00 AM-Saturday': { name: 'Kids Karate', color: '#FED7E2', text: '#C41E3A' },
      '8:00 AM-Sunday': { name: 'Kids Boxing', color: '#FED7E2', text: '#C41E3A' },
      '8:00 AM-Monday': { name: 'Kids Karate', color: '#FED7E2', text: '#C41E3A' },
      '8:00 AM-Tuesday': { name: 'Kids BJJ', color: '#FED7E2', text: '#C41E3A' },
      '8:00 AM-Wednesday': { name: 'Kids Karate', color: '#FED7E2', text: '#C41E3A' },
      '8:00 AM-Thursday': { name: 'Kids Boxing', color: '#FED7E2', text: '#C41E3A' },
      '5:00 PM-Saturday': { name: 'Adult Boxing', color: '#BEE3F8', text: '#2A4365' },
      '5:00 PM-Sunday': { name: 'Adult Karate', color: '#BEE3F8', text: '#2A4365' },
      '5:00 PM-Monday': { name: 'Adult Boxing', color: '#BEE3F8', text: '#2A4365' },
      '5:00 PM-Tuesday': { name: 'Adult BJJ', color: '#BEE3F8', text: '#2A4365' },
      '5:00 PM-Wednesday': { name: 'Adult Boxing', color: '#BEE3F8', text: '#2A4365' },
      '5:00 PM-Thursday': { name: 'Adult Karate', color: '#BEE3F8', text: '#2A4365' },
      '7:00 PM-Saturday': { name: 'Adv Kata', color: '#FEFCBF', text: '#744210' },
      '7:00 PM-Sunday': { name: 'Combat BJJ', color: '#C6F6D5', text: '#22543D' },
      '7:00 PM-Monday': { name: 'Adv Sparring', color: '#FEFCBF', text: '#744210' },
      '7:00 PM-Tuesday': { name: 'Kickboxing', color: '#C6F6D5', text: '#22543D' },
      '7:00 PM-Wednesday': { name: 'Adv Kata', color: '#FEFCBF', text: '#744210' },
      '7:00 PM-Thursday': { name: 'Kickboxing', color: '#C6F6D5', text: '#22543D' },
    };
    return (
      <>
        <PageHeader title="Master Weekly Schedule" subtitle="Bahrain Self Defense Team — Sat to Thu" />
        <div style={cardWrap}>
          <table style={{ ...tableStyle, tableLayout: 'fixed' }}>
            <thead><tr>
              <th style={{ ...th, width: 80 }}>Time</th>
              {days.map(d => <th key={d} style={{ ...th, textAlign: 'center' }}>{d.substring(0, 3)}</th>)}
            </tr></thead>
            <tbody>
              {times.map(t => (
                <tr key={t}>
                  <td style={{ ...td, fontFamily: FONTS.number, fontWeight: 600, fontSize: 12 }}>{t}</td>
                  {days.map(d => {
                    const s = sessionMap[`${t}-${d}`];
                    return (
                      <td key={d} style={{ ...td, padding: 6, textAlign: 'center' }}>
                        {s ? (
                          <div style={{ background: s.color, color: s.text || COLORS.text, borderRadius: 8, padding: '10px 6px', fontSize: 11, fontWeight: 600, fontFamily: FONTS.body }}>
                            {s.name}
                          </div>
                        ) : (
                          <div style={{ color: COLORS.border, fontSize: 11 }}>—</div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {/* Legend */}
          <div style={{ display: 'flex', gap: 20, marginTop: 18, paddingTop: 14, borderTop: `1px solid ${COLORS.border}` }}>
            {[
              { label: 'Kids Classes', color: '#FED7E2' },
              { label: 'Adult Classes', color: '#BEE3F8' },
              { label: 'Advanced / Kata', color: '#FEFCBF' },
              { label: 'Combat / Kickboxing', color: '#C6F6D5' },
              { label: 'Open Gym', color: '#CBD5E0' },
            ].map((l, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontFamily: FONTS.body, color: COLORS.text }}>
                <div style={{ width: 14, height: 14, borderRadius: 4, background: l.color }} />
                {l.label}
              </div>
            ))}
          </div>
        </div>
      </>
    );
  };

  /* ═══════════════════════════════════════
     SCREEN 8 — DAILY SESSIONS
     ═══════════════════════════════════════ */
  const renderDailySessions = () => (
    <>
      <PageHeader title="Today's Sessions" subtitle="Wednesday, February 18, 2026" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {[
          { name: 'Kids Karate', time: '8:00 – 9:00 AM', coach: 'Yousif Salman', attended: '12/15', status: 'Completed', borderColor: COLORS.success, statusBg: COLORS.success },
          { name: 'Adult Boxing', time: '5:00 – 6:30 PM', coach: 'Ahmad Khalil', attended: '8/20', status: 'LIVE', borderColor: '#3182CE', statusBg: COLORS.primary },
          { name: 'BJJ Advanced', time: '7:00 – 8:30 PM', coach: 'Mahmoud Rashid', attended: '—', status: 'Upcoming', borderColor: COLORS.accent, statusBg: COLORS.accent },
          { name: 'Kickboxing', time: '7:00 – 8:30 PM', coach: 'Ali Abdullah (ON LEAVE)', attended: '—', status: 'NO COACH', borderColor: COLORS.error, statusBg: COLORS.error, needsSub: true },
        ].map((s, i) => (
          <div key={i} style={{ ...cardWrap, borderLeft: `5px solid ${s.borderColor}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
              <div>
                <div style={{ fontFamily: FONTS.heading, fontSize: 18, fontWeight: 700, color: COLORS.text }}>{s.name}</div>
                <div style={{ fontSize: 13, color: COLORS.textLight, marginTop: 4, fontFamily: FONTS.body }}>{s.time} &middot; {s.coach}</div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 11, color: COLORS.textLight, textTransform: 'uppercase' }}>Attended</div>
                <div style={{ fontFamily: FONTS.number, fontSize: 20, fontWeight: 700, color: COLORS.text }}>{s.attended}</div>
              </div>
              <span style={badge(s.statusBg)}>{s.status}</span>
              {s.status === 'Upcoming' && !s.needsSub && (
                <button style={btn('#3182CE')} onClick={() => setCurrentScreen('session-kiosk')}>▶ Start Session</button>
              )}
              {s.status === 'LIVE' && (
                <button style={btn(COLORS.primary)} onClick={() => setCurrentScreen('session-kiosk')}>📱 Show QR / Kiosk</button>
              )}
              {s.needsSub && (
                <button style={btn(COLORS.error)} onClick={() => setCurrentScreen('substitute')}>Assign Substitute</button>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  );

  /* ═══════════════════════════════════════
     SCREEN 9 — SUBSTITUTE
     ═══════════════════════════════════════ */
  const renderSubstitute = () => (
    <>
      <Breadcrumb items={[
        { label: 'Sessions', onClick: () => setCurrentScreen('daily-sessions') },
        { label: 'Assign Substitute' },
      ]} />
      <PageHeader title="Assign Substitute Coach" subtitle="Kickboxing session needs coverage" />
      <div style={{ display: 'flex', gap: 20 }}>
        <div style={{ flex: 1 }}>
          {/* Session Details */}
          <div style={{ ...cardWrap, borderLeft: `5px solid ${COLORS.error}` }}>
            <h3 style={sectionTitle}>Session Details</h3>
            <div style={{ fontSize: 13, fontFamily: FONTS.body, color: COLORS.text }}>
              <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 8 }}>
                <strong>Session:</strong><span>Kickboxing</span>
                <strong>Time:</strong><span>7:00 – 8:30 PM</span>
                <strong>Regular Coach:</strong><span>Ali Abdullah</span>
                <strong>Reason:</strong><span style={{ color: COLORS.error }}>On Leave (Medical)</span>
                <strong>Students Expected:</strong><span>18</span>
              </div>
            </div>
          </div>

          {/* Form */}
          <div style={cardWrap}>
            <h3 style={sectionTitle}>Substitute Assignment</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 6 }}>Select Coach</label>
                <select style={selectStyle}>
                  <option>— Choose substitute coach —</option>
                  <option>Fatima Noor (Kickboxing — Available)</option>
                  <option>Yousif Salman (Karate — Available)</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 6 }}>Duration</label>
                <div style={{ display: 'flex', gap: 10 }}>
                  {['Today Only', 'Date Range', 'Permanent'].map((d, i) => (
                    <div key={i} style={{
                      padding: '10px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
                      background: i === 0 ? COLORS.primary : COLORS.bg,
                      color: i === 0 ? '#fff' : COLORS.textLight,
                      cursor: 'pointer', fontFamily: FONTS.body,
                    }}>{d}</div>
                  ))}
                </div>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 6 }}>Notify via</label>
                <div style={{ display: 'flex', gap: 16 }}>
                  {[{ label: 'Push Notification', checked: true }, { label: 'WhatsApp', checked: true }, { label: 'Email', checked: false }].map((n, i) => (
                    <label key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontFamily: FONTS.body, cursor: 'pointer' }}>
                      <input type="checkbox" defaultChecked={n.checked} style={{ accentColor: COLORS.primary }} />{n.label}
                    </label>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button style={btn()}>Assign Substitute</button>
                <button style={btn(COLORS.bg, COLORS.text)} onClick={() => setCurrentScreen('daily-sessions')}>Cancel</button>
              </div>
            </div>
          </div>
        </div>

        {/* Available Coaches */}
        <div style={{ ...cardWrap, flex: 1 }}>
          <h3 style={sectionTitle}>Available Coaches</h3>
          {[
            { name: 'Fatima Noor', discipline: 'Kickboxing', status: 'Available', color: COLORS.success, avatar: 'FN' },
            { name: 'Yousif Salman', discipline: 'Karate', status: 'Available', color: COLORS.success, avatar: 'YS' },
            { name: 'Mahmoud Rashid', discipline: 'BJJ', status: 'Busy (7 PM Session)', color: COLORS.warning, avatar: 'MR' },
          ].map((c, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 0', borderBottom: i < 2 ? `1px solid ${COLORS.border}` : 'none' }}>
              <div style={{ width: 44, height: 44, borderRadius: '50%', background: COLORS.secondary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14, fontWeight: 700, fontFamily: FONTS.body }}>{c.avatar}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14, fontFamily: FONTS.body }}>{c.name}</div>
                <div style={{ fontSize: 12, color: COLORS.textLight }}>{c.discipline}</div>
              </div>
              <span style={badge(c.color)}>{c.status}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  /* ═══════════════════════════════════════
     SCREEN 10 — EXERCISES
     ═══════════════════════════════════════ */
  const renderExercises = () => (
    <>
      <PageHeader title="Exercise Library" subtitle="48 exercises across all disciplines"
        actions={<button style={btn()}>+ Add Exercise</button>}
      />
      <div style={{ ...cardWrap, display: 'flex', gap: 12, alignItems: 'center', marginBottom: 20 }}>
        <input placeholder="Search exercises..." style={{ ...inputStyle, maxWidth: 280 }} />
        <select style={{ ...selectStyle, maxWidth: 150 }}><option>All Disciplines</option><option>Karate</option><option>Boxing</option><option>BJJ</option><option>Kickboxing</option></select>
        <select style={{ ...selectStyle, maxWidth: 140 }}><option>All Levels</option><option>Beginner</option><option>Intermediate</option><option>Advanced</option></select>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16 }}>
        {sampleExercises.map((e, i) => (
          <div key={i} style={cardWrap}>
            {/* Video thumbnail */}
            <div style={{ background: e.color, borderRadius: 8, height: 110, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14, position: 'relative' }}>
              <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'rgba(255,255,255,.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20 }}>▶</div>
              <div style={{ position: 'absolute', top: 8, right: 8 }}><span style={badge('rgba(0,0,0,.6)')}>{e.level}</span></div>
            </div>
            <div style={{ fontFamily: FONTS.heading, fontSize: 15, fontWeight: 700, color: COLORS.text, marginBottom: 6 }}>{e.title}</div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: COLORS.textLight, fontFamily: FONTS.body }}>{e.discipline}</span>
              <span style={{ fontSize: 12, color: COLORS.textLight, fontFamily: FONTS.body }}>Assigned to {e.assigned} students</span>
            </div>
            <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
              <span style={{ fontSize: 12, color: COLORS.primary, cursor: 'pointer', fontFamily: FONTS.body }}>🔗 YouTube Link</span>
            </div>
          </div>
        ))}
      </div>
    </>
  );

  /* ═══════════════════════════════════════
     SCREEN 11 — PAYMENTS
     ═══════════════════════════════════════ */
  const renderPayments = () => (
    <>
      <PageHeader title="Payments" subtitle="Financial transactions and billing"
        actions={<button style={btn()}>+ Record Payment</button>}
      />
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20 }}>
        {['Transactions', 'Overdue (12)', 'Due Soon (5)', 'Plans'].map((t, i) => (
          <div key={i} style={{
            padding: '10px 22px', fontSize: 13, fontWeight: i === 0 ? 700 : 400, fontFamily: FONTS.body,
            cursor: 'pointer', borderBottom: i === 0 ? `3px solid ${COLORS.primary}` : `3px solid transparent`,
            color: i === 0 ? COLORS.primary : COLORS.textLight,
          }}>{t}</div>
        ))}
      </div>
      {/* KPIs */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          { label: 'Collected This Month', value: '3,420 BHD', color: COLORS.success },
          { label: 'Outstanding', value: '1,860 BHD', color: COLORS.error },
          { label: 'Due This Week', value: '540 BHD', color: COLORS.warning },
          { label: 'Benefit Pay Txns', value: '28', color: '#3182CE' },
        ].map((k, i) => (
          <div key={i} style={kpiCard()}>
            <div style={{ fontSize: 12, color: COLORS.textLight, fontFamily: FONTS.body, marginBottom: 6 }}>{k.label}</div>
            <div style={{ fontFamily: FONTS.number, fontSize: 26, fontWeight: 800, color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      <div style={cardWrap}>
        <table style={tableStyle}>
          <thead><tr>
            <th style={th}>Student</th><th style={th}>Plan</th><th style={th}>Amount</th><th style={th}>Due Date</th>
            <th style={th}>Method</th><th style={th}>Status</th><th style={th}>Actions</th>
          </tr></thead>
          <tbody>
            {[
              { student: 'Sara Ahmed', plan: 'Monthly Adult', amount: '55.000', due: 'Feb 10', method: 'Benefit Pay', status: 'Overdue', color: COLORS.error },
              { student: 'Omar Hassan', plan: 'Quarterly BJJ', amount: '120.000', due: 'Feb 15', method: 'Cash', status: 'Paid', color: COLORS.success },
              { student: 'Fahad Al-Dosari', plan: 'Monthly Kids', amount: '45.000', due: 'Feb 20', method: '—', status: 'Due Soon', color: COLORS.warning },
              { student: 'Layla Khalifa', plan: 'Trial', amount: '0.000', due: '—', method: '—', status: 'Free', color: '#3182CE' },
              { student: 'Ali Mansoor', plan: 'Monthly Adult', amount: '55.000', due: 'Jan 28', method: '—', status: 'Overdue', color: COLORS.error },
            ].map((p, i) => (
              <tr key={i}>
                <td style={{ ...td, fontWeight: 600 }}>{p.student}</td>
                <td style={td}>{p.plan}</td>
                <td style={{ ...td, fontFamily: FONTS.number, fontWeight: 600 }}>{p.amount} BHD</td>
                <td style={{ ...td, fontFamily: FONTS.number }}>{p.due}</td>
                <td style={td}>{p.method}</td>
                <td style={td}><span style={badge(p.color)}>{p.status}</span></td>
                <td style={td}>
                  {p.status === 'Overdue' && <button style={{ ...btn(COLORS.error), padding: '4px 10px', fontSize: 11 }}>Remind</button>}
                  {p.status === 'Due Soon' && <button style={{ ...btn(COLORS.warning), padding: '4px 10px', fontSize: 11 }}>Send Link</button>}
                  {p.status === 'Paid' && <span style={{ fontSize: 12, color: COLORS.success }}>✓ Receipt</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );

  /* ═══════════════════════════════════════
     SCREEN 12 — FINANCE
     ═══════════════════════════════════════ */
  const renderFinance = () => (
    <>
      <PageHeader title="Financial Dashboard" subtitle="February 2026"
        actions={<button style={btn('#fff', COLORS.text)}>Export Report</button>}
      />
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          { label: 'Total Revenue', value: '4,820 BHD', color: COLORS.success },
          { label: 'Commission Paid', value: '3,840 BHD', color: COLORS.primary },
          { label: 'Net Profit', value: '980 BHD', color: COLORS.accent },
          { label: 'Outstanding', value: '1,860 BHD', color: COLORS.error },
        ].map((k, i) => (
          <div key={i} style={kpiCard()}>
            <div style={{ fontSize: 12, color: COLORS.textLight, fontFamily: FONTS.body, marginBottom: 6 }}>{k.label}</div>
            <div style={{ fontFamily: FONTS.number, fontSize: 28, fontWeight: 800, color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 20, marginBottom: 24 }}>
        {/* Revenue by Discipline */}
        <div style={{ ...cardWrap, flex: 1 }}>
          <h3 style={sectionTitle}>Revenue by Discipline</h3>
          {[
            { label: 'Karate', pct: 44, value: '2,121 BHD', color: COLORS.primary },
            { label: 'Boxing', pct: 25, value: '1,205 BHD', color: '#3182CE' },
            { label: 'BJJ', pct: 18, value: '868 BHD', color: '#805AD5' },
            { label: 'Kickboxing', pct: 13, value: '627 BHD', color: COLORS.warning },
          ].map((d, i) => (
            <div key={i} style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontFamily: FONTS.body, marginBottom: 4 }}>
                <span style={{ fontWeight: 600, color: COLORS.text }}>{d.label}</span>
                <span style={{ fontFamily: FONTS.number, color: COLORS.textLight }}>{d.value} ({d.pct}%)</span>
              </div>
              <div style={{ height: 10, borderRadius: 5, background: COLORS.border }}>
                <div style={{ width: `${d.pct}%`, height: '100%', borderRadius: 5, background: d.color }} />
              </div>
            </div>
          ))}
        </div>

        {/* Monthly Trend */}
        <div style={{ ...cardWrap, flex: 1 }}>
          <h3 style={sectionTitle}>Monthly Revenue Trend</h3>
          <BarChart
            data={[
              { label: 'Sep', value: 3200, color: COLORS.secondary },
              { label: 'Oct', value: 3650, color: COLORS.secondary },
              { label: 'Nov', value: 3900, color: COLORS.secondary },
              { label: 'Dec', value: 4100, color: COLORS.secondary },
              { label: 'Jan', value: 4300, color: COLORS.secondary },
              { label: 'Feb', value: 4820, color: COLORS.primary },
            ]}
            maxVal={5500}
            height={180}
          />
        </div>
      </div>
    </>
  );

  /* ═══════════════════════════════════════
     SCREEN 13 — WHATSAPP
     ═══════════════════════════════════════ */
  const renderWhatsApp = () => (
    <>
      <PageHeader title="WhatsApp Integration" subtitle="Automated messaging and payment links"
        actions={<button style={btn('#25D366')}>Send Broadcast</button>}
      />
      <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
        {[
          { label: 'Messages Sent (Feb)', value: '342', color: '#25D366' },
          { label: 'Delivery Rate', value: '98.2%', color: COLORS.success },
          { label: 'Payments via Link', value: '18', color: COLORS.primary },
        ].map((k, i) => (
          <div key={i} style={kpiCard()}>
            <div style={{ fontSize: 12, color: COLORS.textLight, fontFamily: FONTS.body, marginBottom: 6 }}>{k.label}</div>
            <div style={{ fontFamily: FONTS.number, fontSize: 28, fontWeight: 800, color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 20, marginBottom: 24 }}>
        {/* Templates */}
        <div style={{ ...cardWrap, flex: 1 }}>
          <h3 style={sectionTitle}>Message Templates</h3>
          {[
            { name: 'Payment Reminder', desc: 'Sent 5 days before due date', sent: 45, icon: '💳' },
            { name: 'Session Reminder', desc: 'Sent 2 hours before session', sent: 186, icon: '⏰' },
            { name: 'Welcome Message', desc: 'Sent on new registration', sent: 12, icon: '👋' },
            { name: 'Membership Expiry', desc: 'Sent 7 days before expiry', sent: 8, icon: '📅' },
          ].map((t, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 0', borderBottom: i < 3 ? `1px solid ${COLORS.border}` : 'none' }}>
              <span style={{ fontSize: 24 }}>{t.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14, fontFamily: FONTS.body, color: COLORS.text }}>{t.name}</div>
                <div style={{ fontSize: 12, color: COLORS.textLight }}>{t.desc}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: FONTS.number, fontWeight: 700, fontSize: 16 }}>{t.sent}</div>
                <div style={{ fontSize: 10, color: COLORS.textLight }}>sent</div>
              </div>
              <button style={{ ...btn(COLORS.bg, COLORS.primary), padding: '4px 10px', fontSize: 11, border: `1px solid ${COLORS.border}` }}>Edit</button>
            </div>
          ))}
        </div>

        {/* Auto-Reminder Settings */}
        <div style={{ ...cardWrap, flex: 1 }}>
          <h3 style={sectionTitle}>Auto-Reminder Settings</h3>
          {[
            { label: 'Payment Due Reminder', value: '5 days before', enabled: true },
            { label: 'Session Reminder', value: '2 hours before', enabled: true },
            { label: 'Membership Expiry', value: '7 days before', enabled: true },
            { label: 'Overdue Follow-up', value: 'Every 3 days', enabled: true },
            { label: 'Birthday Greeting', value: 'On birthday', enabled: false },
            { label: 'Belt Exam Ready', value: 'At 200 hours', enabled: true },
          ].map((s, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 0', borderBottom: i < 5 ? `1px solid ${COLORS.border}` : 'none' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, fontFamily: FONTS.body, color: COLORS.text }}>{s.label}</div>
                <div style={{ fontSize: 11, color: COLORS.textLight }}>{s.value}</div>
              </div>
              <div style={{
                width: 44, height: 24, borderRadius: 12,
                background: s.enabled ? '#25D366' : COLORS.border,
                position: 'relative', cursor: 'pointer',
              }}>
                <div style={{
                  width: 18, height: 18, borderRadius: '50%', background: '#fff',
                  position: 'absolute', top: 3,
                  left: s.enabled ? 23 : 3,
                  transition: 'left .2s',
                  boxShadow: '0 1px 3px rgba(0,0,0,.2)',
                }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  /* ═══════════════════════════════════════
     SCREEN 14 — SETTINGS
     ═══════════════════════════════════════ */
  const renderSettings = () => (
    <>
      <PageHeader title="Settings" subtitle="Manage your gym configuration" />
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 0, marginBottom: 20 }}>
        {['Gym Profile', 'Plans', 'Hours', 'Integrations', 'Admin Users'].map((t, i) => (
          <div key={i} style={{
            padding: '10px 22px', fontSize: 13, fontWeight: i === 0 ? 700 : 400, fontFamily: FONTS.body,
            cursor: 'pointer', borderBottom: i === 0 ? `3px solid ${COLORS.primary}` : `3px solid transparent`,
            color: i === 0 ? COLORS.primary : COLORS.textLight,
          }}>{t}</div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 20 }}>
        {/* Gym Profile Form */}
        <div style={{ ...cardWrap, flex: 1 }}>
          <h3 style={sectionTitle}>Gym Profile</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {[
              { label: 'Gym Name', value: 'Bahrain Self Defense Team' },
              { label: 'Address', value: 'Building 123, Road 456, Block 789, Manama, Bahrain' },
              { label: 'Phone', value: '+973 1700 1234' },
              { label: 'Email', value: 'admin@bsdt.bh' },
              { label: 'Currency', value: 'BHD (Bahraini Dinar)', select: true },
            ].map((f, i) => (
              <div key={i}>
                <label style={{ fontSize: 13, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 6 }}>{f.label}</label>
                {f.select
                  ? <select style={selectStyle} defaultValue={f.value}><option>{f.value}</option></select>
                  : <input style={inputStyle} defaultValue={f.value} />
                }
              </div>
            ))}
            <button style={{ ...btn(), alignSelf: 'flex-start' }}>Save Changes</button>
          </div>
        </div>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 20 }}>
          {/* Integrations */}
          <div style={cardWrap}>
            <h3 style={sectionTitle}>Integrations</h3>
            {[
              { name: 'Tap Payments', status: 'Connected', color: COLORS.success, icon: '💳' },
              { name: 'WhatsApp Business API', status: 'Active', color: COLORS.success, icon: '📱' },
              { name: 'Firebase', status: 'Active', color: COLORS.success, icon: '🔥' },
            ].map((intg, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0', borderBottom: i < 2 ? `1px solid ${COLORS.border}` : 'none' }}>
                <span style={{ fontSize: 22 }}>{intg.icon}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, fontFamily: FONTS.body }}>{intg.name}</div>
                </div>
                <span style={badge(intg.color)}>{intg.status}</span>
                <button style={{ ...btn(COLORS.bg, COLORS.text), padding: '4px 10px', fontSize: 11, border: `1px solid ${COLORS.border}` }}>Configure</button>
              </div>
            ))}
          </div>

          {/* Membership Plans */}
          <div style={cardWrap}>
            <h3 style={sectionTitle}>Membership Plans</h3>
            <table style={tableStyle}>
              <thead><tr><th style={th}>Plan</th><th style={th}>Price</th><th style={th}>Actions</th></tr></thead>
              <tbody>
                {[
                  { name: 'Monthly Kids', price: '45.000 BHD' },
                  { name: 'Monthly Adult', price: '55.000 BHD' },
                  { name: 'Quarterly', price: '120.000 BHD' },
                  { name: 'Yearly', price: '400.000 BHD' },
                  { name: 'Trial (1 week)', price: 'Free' },
                ].map((p, i) => (
                  <tr key={i}>
                    <td style={{ ...td, fontWeight: 600 }}>{p.name}</td>
                    <td style={{ ...td, fontFamily: FONTS.number }}>{p.price}</td>
                    <td style={td}><button style={{ ...btn(COLORS.bg, COLORS.primary), padding: '4px 10px', fontSize: 11, border: `1px solid ${COLORS.border}` }}>Edit</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );

  /* ═══════════════════════════════════════
     SCREEN 15 — ADD STUDENT
     ═══════════════════════════════════════ */
  const renderAddStudent = () => (
    <>
      <Breadcrumb items={[
        { label: 'Students', onClick: () => setCurrentScreen('students') },
        { label: 'Add New Student' },
      ]} />
      <PageHeader title="Add New Student" subtitle="Fill in all required fields to register a new student" />
      <div style={{ display: 'flex', gap: 20 }}>
        <div style={{ ...cardWrap, flex: 2 }}>
          {/* Photo Upload */}
          <div style={{ display: 'flex', gap: 20, marginBottom: 24 }}>
            <div style={{
              width: 120, height: 120, borderRadius: 12, border: `2px dashed ${COLORS.border}`,
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer', background: COLORS.bg,
            }}>
              <span style={{ fontSize: 28, marginBottom: 4 }}>📷</span>
              <span style={{ fontSize: 11, color: COLORS.textLight, fontFamily: FONTS.body }}>Upload Photo</span>
            </div>
            <div style={{ flex: 1 }}>
              <h3 style={{ ...sectionTitle, marginBottom: 12 }}>Personal Information</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 4 }}>First Name *</label>
                  <input style={inputStyle} placeholder="Enter first name" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 4 }}>Last Name *</label>
                  <input style={inputStyle} placeholder="Enter last name" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 4 }}>Date of Birth *</label>
                  <input style={inputStyle} type="date" />
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 4 }}>Gender *</label>
                  <select style={selectStyle}><option>Select gender</option><option>Male</option><option>Female</option></select>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <h3 style={{ ...sectionTitle, marginBottom: 12 }}>Contact Information</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 24 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 4 }}>Phone</label>
              <input style={inputStyle} placeholder="+973 XXXX XXXX" />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 4 }}>Email</label>
              <input style={inputStyle} placeholder="student@email.com" type="email" />
            </div>
          </div>

          {/* Guardian Info */}
          <h3 style={{ ...sectionTitle, marginBottom: 12 }}>Guardian Information</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 24 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 4 }}>Guardian Name</label>
              <input style={inputStyle} placeholder="Enter guardian name" />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 4 }}>Guardian Phone</label>
              <input style={inputStyle} placeholder="+973 XXXX XXXX" />
            </div>
            <div style={{ gridColumn: 'span 2' }}>
              <label style={{ fontSize: 12, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 4 }}>Guardian Email</label>
              <input style={inputStyle} placeholder="guardian@email.com" type="email" />
            </div>
          </div>

          {/* Training Info */}
          <h3 style={{ ...sectionTitle, marginBottom: 12 }}>Training Details</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 24 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 4 }}>Discipline *</label>
              <select style={selectStyle}><option>Select discipline</option><option>Karate</option><option>Boxing</option><option>BJJ</option><option>Kickboxing</option></select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 4 }}>Assigned Coach *</label>
              <select style={selectStyle}><option>Select coach</option><option>Yousif Salman</option><option>Ahmad Khalil</option><option>Mahmoud Rashid</option><option>Ali Abdullah</option></select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 4 }}>Belt Level</label>
              <select style={selectStyle}><option>White (Beginner)</option><option>Yellow</option><option>Orange</option><option>Green</option><option>Blue</option><option>Brown</option><option>Black</option></select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 4 }}>Membership Plan *</label>
              <select style={selectStyle}><option>Select plan</option><option>Monthly Kids — 45 BHD</option><option>Monthly Adult — 55 BHD</option><option>Quarterly — 120 BHD</option><option>Yearly — 400 BHD</option><option>Trial (Free)</option></select>
            </div>
          </div>

          {/* Notes */}
          <div style={{ marginBottom: 24 }}>
            <label style={{ fontSize: 12, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 4 }}>Notes</label>
            <textarea style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }} placeholder="Any additional notes about the student..." />
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: 12 }}>
            <button style={btn()}>Save Student</button>
            <button style={btn(COLORS.bg, COLORS.text)} onClick={() => setCurrentScreen('students')}>Cancel</button>
          </div>
        </div>

        {/* Summary Sidebar */}
        <div style={{ flex: 1 }}>
          <div style={cardWrap}>
            <h3 style={sectionTitle}>Registration Summary</h3>
            <div style={{ background: COLORS.bg, borderRadius: 8, padding: 16, fontSize: 13, fontFamily: FONTS.body, color: COLORS.textLight }}>
              <p style={{ margin: '0 0 8px' }}>Fill in the form to see a summary of the new student registration.</p>
              <p style={{ margin: '0 0 8px' }}>Required fields are marked with *</p>
              <p style={{ margin: 0 }}>A welcome WhatsApp message will be sent automatically after registration.</p>
            </div>
          </div>
          <div style={cardWrap}>
            <h3 style={sectionTitle}>Quick Tips</h3>
            {[
              'Students under 16 require guardian information',
              'Belt level defaults to White for new students',
              'Trial plans are limited to 7 days',
              'Payment reminders are sent automatically via WhatsApp',
            ].map((tip, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, padding: '8px 0', fontSize: 12, fontFamily: FONTS.body, color: COLORS.text, borderBottom: i < 3 ? `1px solid ${COLORS.border}` : 'none' }}>
                <span style={{ color: COLORS.primary }}>•</span>{tip}
              </div>
            ))}
          </div>
        </div>
      </div>
    </>
  );

  /* ═══════════════════════════════════════
     SCREEN 16 — ATTENDANCE
     ═══════════════════════════════════════ */
  const renderAttendance = () => {
    const calDays = Array.from({ length: 28 }, (_, i) => i + 1);
    const attendanceColors = {
      high: COLORS.success, medium: COLORS.warning, low: COLORS.error, none: COLORS.border,
    };
    // Random-ish pattern for calendar
    const dayStatus = (d) => {
      if ([5, 6, 12, 13, 19, 20, 26, 27].includes(d)) return 'none'; // Fri/Sat off
      if (d > 18) return 'none'; // future
      if ([1, 3, 8, 10, 15, 17].includes(d)) return 'high';
      if ([2, 9, 16].includes(d)) return 'medium';
      return 'low';
    };
    return (
      <>
        <PageHeader title="Attendance Overview" subtitle="Track student and session attendance"
          actions={<button style={btn()}>Export Report</button>}
        />
        {/* Live Check-In Feed */}
        <div style={{ ...cardWrap, marginBottom: 20, borderLeft: `5px solid ${COLORS.success}` }}>
          <h3 style={sectionTitle}>Live Check-In Feed</h3>
          {[
            { name: 'Fahad Al-Dosari', method: 'QR', time: '2 min ago', icon: '📱' },
            { name: 'Omar Hassan', method: 'Fingerprint', time: '8 min ago', icon: '👆' },
            { name: 'Sara Ahmed', method: 'QR', time: '12 min ago', icon: '📱' },
            { name: 'Layla Khalifa', method: 'Manual', time: '15 min ago', icon: '✏️' },
          ].map((c, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 0', borderBottom: i < 3 ? `1px solid ${COLORS.border}` : 'none' }}>
              <span style={{
                fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 6, fontFamily: FONTS.body,
                background: c.method === 'QR' ? '#EBF8FF' : c.method === 'Fingerprint' ? '#F0FFF4' : COLORS.bg,
                color: c.method === 'QR' ? '#3182CE' : c.method === 'Fingerprint' ? COLORS.success : COLORS.textLight,
              }}>{c.icon} {c.method}</span>
              <span style={{ fontSize: 13, fontWeight: 600, fontFamily: FONTS.body, color: COLORS.text }}>{c.name}</span>
              <span style={{ fontSize: 12, color: COLORS.textLight, fontFamily: FONTS.body }}>checked in via {c.method}</span>
              <span style={{ marginLeft: 'auto', fontSize: 11, color: COLORS.textLight, fontFamily: FONTS.body, whiteSpace: 'nowrap' }}>{c.time}</span>
            </div>
          ))}
        </div>

        {/* Toggle */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {['Daily', 'Weekly', 'Monthly'].map((t, i) => (
            <div key={i} style={{
              padding: '8px 20px', borderRadius: 8, fontSize: 13, fontWeight: 600, fontFamily: FONTS.body,
              background: i === 2 ? COLORS.primary : COLORS.bg,
              color: i === 2 ? '#fff' : COLORS.textLight,
              cursor: 'pointer',
            }}>{t}</div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 20, marginBottom: 24 }}>
          {/* Calendar View */}
          <div style={{ ...cardWrap, flex: 1 }}>
            <h3 style={sectionTitle}>February 2026 — Attendance Calendar</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 6, marginBottom: 12 }}>
              {['Sat', 'Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri'].map(d => (
                <div key={d} style={{ textAlign: 'center', fontSize: 11, fontWeight: 600, color: COLORS.textLight, padding: 4, fontFamily: FONTS.body }}>{d}</div>
              ))}
              {calDays.map(d => {
                const st = dayStatus(d);
                return (
                  <div key={d} style={{
                    textAlign: 'center', padding: '8px 4px', borderRadius: 6, fontSize: 12,
                    fontFamily: FONTS.number, fontWeight: 600,
                    background: st === 'none' ? '#F7FAFC' : st === 'high' ? '#C6F6D5' : st === 'medium' ? '#FEFCBF' : '#FED7D7',
                    color: st === 'none' ? COLORS.border : COLORS.text,
                    cursor: st !== 'none' ? 'pointer' : 'default',
                  }}>{d}</div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 16, fontSize: 11, fontFamily: FONTS.body }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><div style={{ width: 12, height: 12, borderRadius: 3, background: '#C6F6D5' }} /> 85%+</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><div style={{ width: 12, height: 12, borderRadius: 3, background: '#FEFCBF' }} /> 70-84%</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><div style={{ width: 12, height: 12, borderRadius: 3, background: '#FED7D7' }} /> Below 70%</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><div style={{ width: 12, height: 12, borderRadius: 3, background: '#F7FAFC', border: `1px solid ${COLORS.border}` }} /> No Session</div>
            </div>
          </div>

          {/* Per-Session Breakdown */}
          <div style={{ ...cardWrap, flex: 1 }}>
            <h3 style={sectionTitle}>Per-Session Breakdown (Today)</h3>
            <table style={tableStyle}>
              <thead><tr><th style={th}>Session</th><th style={th}>Expected</th><th style={th}>Present</th><th style={th}>Absent</th><th style={th}>Rate</th></tr></thead>
              <tbody>
                {[
                  { session: 'Kids Karate', expected: 15, present: 12, absent: 3, rate: '80%' },
                  { session: 'Adult Boxing', expected: 20, present: 8, absent: 12, rate: '40%' },
                  { session: 'BJJ Advanced', expected: 10, present: 0, absent: 0, rate: '—' },
                  { session: 'Kickboxing', expected: 18, present: 0, absent: 0, rate: '—' },
                ].map((s, i) => (
                  <tr key={i}>
                    <td style={{ ...td, fontWeight: 600 }}>{s.session}</td>
                    <td style={{ ...td, fontFamily: FONTS.number }}>{s.expected}</td>
                    <td style={{ ...td, fontFamily: FONTS.number, color: COLORS.success }}>{s.present}</td>
                    <td style={{ ...td, fontFamily: FONTS.number, color: COLORS.error }}>{s.absent}</td>
                    <td style={{ ...td, fontFamily: FONTS.number, fontWeight: 700 }}>{s.rate}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Student-Level Report */}
        <div style={cardWrap}>
          <h3 style={sectionTitle}>Student Attendance Report — February 2026</h3>
          <table style={tableStyle}>
            <thead><tr><th style={th}>Student</th><th style={th}>Discipline</th><th style={th}>Sessions</th><th style={th}>Present</th><th style={th}>Absent</th><th style={th}>Rate</th><th style={th}>Method</th><th style={th}>Trend</th></tr></thead>
            <tbody>
              {[
                { name: 'Fahad Al-Dosari', disc: 'Karate', sess: 12, present: 11, absent: 1, rate: '92%', trend: '↑', method: 'QR' },
                { name: 'Sara Ahmed', disc: 'Boxing', sess: 10, present: 9, absent: 1, rate: '90%', trend: '→', method: 'QR' },
                { name: 'Omar Hassan', disc: 'BJJ', sess: 8, present: 8, absent: 0, rate: '100%', trend: '↑', method: 'Fingerprint' },
                { name: 'Layla Khalifa', disc: 'Karate', sess: 12, present: 12, absent: 0, rate: '100%', trend: '↑', method: 'QR' },
                { name: 'Ali Mansoor', disc: 'Kickboxing', sess: 10, present: 6, absent: 4, rate: '60%', trend: '↓', method: 'Manual' },
              ].map((s, i) => (
                <tr key={i}>
                  <td style={{ ...td, fontWeight: 600 }}>{s.name}</td>
                  <td style={td}>{s.disc}</td>
                  <td style={{ ...td, fontFamily: FONTS.number }}>{s.sess}</td>
                  <td style={{ ...td, fontFamily: FONTS.number, color: COLORS.success }}>{s.present}</td>
                  <td style={{ ...td, fontFamily: FONTS.number, color: s.absent > 2 ? COLORS.error : COLORS.text }}>{s.absent}</td>
                  <td style={{ ...td, fontFamily: FONTS.number, fontWeight: 700 }}>{s.rate}</td>
                  <td style={td}>
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, fontFamily: FONTS.body,
                      background: s.method === 'QR' ? '#EBF8FF' : s.method === 'Fingerprint' ? '#F0FFF4' : COLORS.bg,
                      color: s.method === 'QR' ? '#3182CE' : s.method === 'Fingerprint' ? COLORS.success : COLORS.textLight,
                    }}>{s.method === 'QR' ? '📱' : s.method === 'Fingerprint' ? '👆' : '✏️'} {s.method}</span>
                  </td>
                  <td style={{ ...td, fontSize: 18, color: s.trend === '↑' ? COLORS.success : s.trend === '↓' ? COLORS.error : COLORS.textLight }}>{s.trend}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  /* ═══════════════════════════════════════
     SCREEN 17 — REPORTS
     ═══════════════════════════════════════ */
  const renderReports = () => (
    <>
      <PageHeader title="Reports & Analytics" subtitle="Comprehensive business intelligence"
        actions={<div style={{ display: 'flex', gap: 8 }}>
          <button style={btn('#fff', COLORS.text)}>Export Excel</button>
          <button style={btn(COLORS.primary)}>Export PDF</button>
        </div>}
      />

      {/* Student Reports */}
      <div style={{ display: 'flex', gap: 20, marginBottom: 24 }}>
        <div style={{ ...cardWrap, flex: 1 }}>
          <h3 style={sectionTitle}>Student Enrollment Trend</h3>
          <BarChart
            data={[
              { label: 'Sep', value: 98, color: '#3182CE' },
              { label: 'Oct', value: 108, color: '#3182CE' },
              { label: 'Nov', value: 115, color: '#3182CE' },
              { label: 'Dec', value: 122, color: '#3182CE' },
              { label: 'Jan', value: 134, color: '#3182CE' },
              { label: 'Feb', value: 142, color: COLORS.primary },
            ]}
            maxVal={160}
          />
          <div style={{ marginTop: 12, fontSize: 12, color: COLORS.textLight, fontFamily: FONTS.body }}>
            +44 students (+45%) over 6 months. Average monthly growth: 7.3 students.
          </div>
        </div>

        <div style={{ ...cardWrap, flex: 1 }}>
          <h3 style={sectionTitle}>Churn Rate Analysis</h3>
          {/* Simple pie representation */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 24, marginTop: 12 }}>
            <div style={{
              width: 130, height: 130, borderRadius: '50%',
              background: `conic-gradient(${COLORS.success} 0% 78%, ${COLORS.warning} 78% 90%, ${COLORS.error} 90% 100%)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <div style={{ width: 80, height: 80, borderRadius: '50%', background: COLORS.card, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
                <div style={{ fontFamily: FONTS.number, fontSize: 22, fontWeight: 800, color: COLORS.success }}>78%</div>
                <div style={{ fontSize: 9, color: COLORS.textLight }}>Retained</div>
              </div>
            </div>
            <div>
              {[
                { label: 'Active & Retained', pct: '78%', color: COLORS.success, count: 111 },
                { label: 'At Risk (low attendance)', pct: '12%', color: COLORS.warning, count: 17 },
                { label: 'Churned (inactive)', pct: '10%', color: COLORS.error, count: 14 },
              ].map((s, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 13, fontFamily: FONTS.body }}>
                  <div style={{ width: 12, height: 12, borderRadius: 3, background: s.color }} />
                  <span style={{ color: COLORS.text }}>{s.label}</span>
                  <span style={{ fontFamily: FONTS.number, fontWeight: 700 }}>{s.pct}</span>
                  <span style={{ color: COLORS.textLight, fontSize: 11 }}>({s.count})</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Financial & Performance Reports */}
      <div style={{ display: 'flex', gap: 20, marginBottom: 24 }}>
        <div style={{ ...cardWrap, flex: 1 }}>
          <h3 style={sectionTitle}>Revenue Trend (6 Months)</h3>
          <BarChart
            data={[
              { label: 'Sep', value: 3200, color: COLORS.success },
              { label: 'Oct', value: 3650, color: COLORS.success },
              { label: 'Nov', value: 3900, color: COLORS.success },
              { label: 'Dec', value: 4100, color: COLORS.success },
              { label: 'Jan', value: 4300, color: COLORS.success },
              { label: 'Feb', value: 4820, color: COLORS.primary },
            ]}
            maxVal={5500}
            height={140}
          />
        </div>

        <div style={{ ...cardWrap, flex: 1 }}>
          <h3 style={sectionTitle}>Revenue by Plan Type</h3>
          {[
            { label: 'Monthly Adult', pct: 42, value: '2,024 BHD', color: COLORS.primary },
            { label: 'Monthly Kids', pct: 28, value: '1,350 BHD', color: '#3182CE' },
            { label: 'Quarterly', pct: 18, value: '868 BHD', color: COLORS.warning },
            { label: 'Yearly', pct: 12, value: '578 BHD', color: COLORS.success },
          ].map((d, i) => (
            <div key={i} style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, fontFamily: FONTS.body, marginBottom: 4 }}>
                <span style={{ fontWeight: 600 }}>{d.label}</span>
                <span style={{ fontFamily: FONTS.number, color: COLORS.textLight }}>{d.value} ({d.pct}%)</span>
              </div>
              <div style={{ height: 8, borderRadius: 4, background: COLORS.border }}>
                <div style={{ width: `${d.pct}%`, height: '100%', borderRadius: 4, background: d.color }} />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Attendance Patterns & Instructor Performance */}
      <div style={{ display: 'flex', gap: 20, marginBottom: 24 }}>
        <div style={{ ...cardWrap, flex: 1 }}>
          <h3 style={sectionTitle}>Attendance Patterns</h3>
          <BarChart
            data={[
              { label: 'Sat', value: 82, color: '#3182CE' },
              { label: 'Sun', value: 88, color: '#3182CE' },
              { label: 'Mon', value: 91, color: COLORS.success },
              { label: 'Tue', value: 85, color: '#3182CE' },
              { label: 'Wed', value: 90, color: COLORS.success },
              { label: 'Thu', value: 78, color: COLORS.warning },
            ]}
            maxVal={100}
            height={130}
          />
          <div style={{ marginTop: 10, fontSize: 12, color: COLORS.textLight, fontFamily: FONTS.body }}>
            Average attendance rate by day of week. Monday and Wednesday are peak days.
          </div>
        </div>

        <div style={{ ...cardWrap, flex: 1 }}>
          <h3 style={sectionTitle}>Instructor Performance</h3>
          <table style={tableStyle}>
            <thead><tr><th style={th}>Coach</th><th style={th}>Avg Attend%</th><th style={th}>Student Rating</th><th style={th}>Sessions</th></tr></thead>
            <tbody>
              {[
                { name: 'Yousif Salman', attend: '92%', rating: '4.8 ★', sessions: 18 },
                { name: 'Ahmad Khalil', attend: '88%', rating: '4.6 ★', sessions: 16 },
                { name: 'Mahmoud Rashid', attend: '90%', rating: '4.7 ★', sessions: 14 },
                { name: 'Ali Abdullah', attend: '—', rating: '4.3 ★', sessions: 0 },
              ].map((c, i) => (
                <tr key={i}>
                  <td style={{ ...td, fontWeight: 600 }}>{c.name}</td>
                  <td style={{ ...td, fontFamily: FONTS.number }}>{c.attend}</td>
                  <td style={{ ...td, color: COLORS.accent }}>{c.rating}</td>
                  <td style={{ ...td, fontFamily: FONTS.number }}>{c.sessions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );

  /* ═══════════════════════════════════════
     SCREEN 18 — SESSION KIOSK
     ═══════════════════════════════════════ */
  const renderSessionKiosk = () => (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: COLORS.secondary, color: '#fff', fontFamily: FONTS.body }}>
      {/* Top bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 24px', borderBottom: '1px solid rgba(255,255,255,.1)' }}>
        <div>
          <div style={{ fontFamily: FONTS.heading, fontSize: 20, fontWeight: 700 }}>Adult Boxing</div>
          <div style={{ fontSize: 13, opacity: .7 }}>5:00 – 6:30 PM &middot; Coach Ahmad Khalil</div>
        </div>
        <button onClick={() => setCurrentScreen('dashboard')}
          style={{ ...btn('rgba(255,255,255,.15)', '#fff'), padding: '10px 20px', fontSize: 13, minHeight: 48 }}>
          ✕ Exit Kiosk
        </button>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, display: 'flex', padding: 24, gap: 24, overflow: 'hidden' }}>
        {/* Left: QR / Biometric */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          {kioskMode === 'qr' ? (
            <>
              {/* Large QR Code */}
              <div style={{ width: 300, height: 300, background: '#fff', borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(11,1fr)', gap: 3, width: 240, height: 240 }}>
                  {Array.from({ length: 121 }).map((_, i) => (
                    <div key={i} style={{ background: Math.random() > 0.4 ? COLORS.secondary : '#fff', borderRadius: 2 }} />
                  ))}
                </div>
              </div>
              <div style={{ fontFamily: FONTS.body, fontSize: 16, opacity: .7, marginBottom: 4 }}>Scan to check in</div>
              <div style={{ fontFamily: FONTS.body, fontSize: 13, opacity: .5 }}>QR refreshes every 30 seconds</div>
            </>
          ) : (
            <>
              {/* Biometric Mode */}
              <div style={{
                width: 200, height: 200, borderRadius: '50%',
                background: 'rgba(196,30,58,.15)', border: `3px solid ${COLORS.primary}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                marginBottom: 20, animation: 'pulse 2s infinite',
              }}>
                <span style={{ fontSize: 80 }}>👆</span>
              </div>
              <div style={{ fontFamily: FONTS.heading, fontSize: 20, fontWeight: 700, marginBottom: 4 }}>Place finger on scanner</div>
              <div style={{ fontFamily: FONTS.body, fontSize: 14, opacity: .5 }}>Or use facial recognition</div>
              <style>{`@keyframes pulse { 0%, 100% { transform: scale(1); opacity: 1; } 50% { transform: scale(1.05); opacity: .85; } }`}</style>
            </>
          )}

          {/* Counter */}
          <div style={{ marginTop: 32, textAlign: 'center' }}>
            <div style={{ fontFamily: FONTS.number, fontSize: 48, fontWeight: 800, color: COLORS.accent }}>
              12 <span style={{ fontSize: 24, color: 'rgba(255,255,255,.5)' }}>/ 20</span>
            </div>
            <div style={{ fontSize: 14, opacity: .7, marginBottom: 12 }}>checked in</div>
            {/* Progress bar */}
            <div style={{ width: 280, height: 10, borderRadius: 5, background: 'rgba(255,255,255,.15)', margin: '0 auto' }}>
              <div style={{ width: '60%', height: '100%', borderRadius: 5, background: COLORS.success }} />
            </div>
          </div>

          {/* Mode toggle */}
          <div style={{ display: 'flex', gap: 8, marginTop: 24 }}>
            {[
              { key: 'qr', label: '📱 QR Mode' },
              { key: 'biometric', label: '👆 Biometric' },
            ].map(m => (
              <button key={m.key} onClick={() => setKioskMode(m.key)}
                style={{
                  padding: '12px 24px', borderRadius: 10, border: 'none', cursor: 'pointer',
                  fontFamily: FONTS.body, fontWeight: 600, fontSize: 14, minHeight: 48,
                  background: kioskMode === m.key ? COLORS.primary : 'rgba(255,255,255,.1)',
                  color: '#fff',
                }}>
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {/* Right: Live check-in feed */}
        <div style={{ width: 300, background: 'rgba(255,255,255,.05)', borderRadius: 16, padding: 16, overflowY: 'auto' }}>
          <div style={{ fontFamily: FONTS.heading, fontSize: 14, fontWeight: 700, marginBottom: 12, opacity: .9 }}>Live Check-ins</div>
          {[
            { name: 'Fahad Al-Dosari', time: '5:02 PM', method: 'QR' },
            { name: 'Sara Ahmed', time: '5:03 PM', method: 'QR' },
            { name: 'Omar Hassan', time: '5:04 PM', method: 'Fingerprint' },
            { name: 'Layla Khalifa', time: '5:05 PM', method: 'QR' },
            { name: 'Ali Mansoor', time: '5:06 PM', method: 'QR' },
            { name: 'Noor Khalid', time: '5:07 PM', method: 'Fingerprint' },
            { name: 'Hassan Ibrahim', time: '5:08 PM', method: 'QR' },
            { name: 'Maryam Jaber', time: '5:09 PM', method: 'QR' },
            { name: 'Khalid Tariq', time: '5:10 PM', method: 'Manual' },
            { name: 'Yusuf Nasser', time: '5:11 PM', method: 'QR' },
            { name: 'Fatima Rashid', time: '5:12 PM', method: 'Fingerprint' },
            { name: 'Ahmed Bakir', time: '5:14 PM', method: 'QR' },
          ].map((c, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,.06)' }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: COLORS.primary, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: '#fff', fontFamily: FONTS.body }}>
                {c.name.split(' ').map(n => n[0]).join('')}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{c.name}</div>
                <div style={{ fontSize: 11, opacity: .5 }}>{c.time}</div>
              </div>
              <span style={{
                fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 6,
                background: c.method === 'QR' ? 'rgba(49,130,206,.2)' : c.method === 'Fingerprint' ? 'rgba(56,161,105,.2)' : 'rgba(255,255,255,.1)',
                color: c.method === 'QR' ? '#63B3ED' : c.method === 'Fingerprint' ? '#68D391' : 'rgba(255,255,255,.6)',
              }}>
                {c.method === 'QR' ? '📱' : c.method === 'Fingerprint' ? '👆' : '✏️'} {c.method}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  /* ═══════════════════════════════════════
     SCREEN — ADMIN LOGIN
     ═══════════════════════════════════════ */
  const renderAdminLogin = () => (
    <div style={{ width: 440, background: COLORS.card, borderRadius: 16, boxShadow: '0 8px 40px rgba(0,0,0,.14)', overflow: 'hidden' }}>
      {/* Header band */}
      <div style={{ background: COLORS.secondary, padding: '32px 40px 28px', textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 8 }}>🥋</div>
        <div style={{ fontFamily: FONTS.heading, fontSize: 22, fontWeight: 800, color: '#fff', marginBottom: 4 }}>DojoMaster</div>
        <div style={{ fontFamily: FONTS.body, fontSize: 12, color: 'rgba(255,255,255,.6)', letterSpacing: '.5px' }}>ADMIN PORTAL</div>
      </div>

      <div style={{ padding: '32px 40px' }}>
        {/* Role tabs */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 28, borderRadius: 8, overflow: 'hidden', border: `1px solid ${COLORS.border}` }}>
          {['Admin', 'Manager'].map((r, i) => (
            <div key={r} style={{ flex: 1, textAlign: 'center', padding: '10px 0', fontSize: 13, fontFamily: FONTS.body, fontWeight: 600, cursor: 'pointer', background: i === 0 ? COLORS.primary : COLORS.bg, color: i === 0 ? '#fff' : COLORS.textLight }}>{r}</div>
          ))}
        </div>

        <div style={{ marginBottom: 16 }}>
          <label style={{ fontSize: 12, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 6, color: COLORS.textLight }}>Email Address</label>
          <input style={inputStyle} placeholder="admin@dojomaster.bh" type="email" />
        </div>
        <div style={{ marginBottom: 8 }}>
          <label style={{ fontSize: 12, fontWeight: 600, fontFamily: FONTS.body, display: 'block', marginBottom: 6, color: COLORS.textLight }}>Password</label>
          <input style={inputStyle} placeholder="••••••••" type="password" />
        </div>
        <div style={{ textAlign: 'right', marginBottom: 24 }}>
          <span style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.primary, cursor: 'pointer', fontWeight: 600 }}>Forgot Password?</span>
        </div>
        <button style={{ ...btn(), width: '100%', padding: '13px 0', fontSize: 15 }}>Sign In</button>

        <div style={{ marginTop: 28, paddingTop: 20, borderTop: `1px solid ${COLORS.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: FONTS.body, fontSize: 11, color: COLORS.textLight }}>v1.0 &middot; Bahrain Self Defense Team</span>
          <span onClick={() => setCurrentScreen('dashboard')} style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.primary, cursor: 'pointer', fontWeight: 600 }}>→ Skip to Dashboard</span>
        </div>
      </div>
    </div>
  );

  /* ═══════════════════════════════════════
     SCREEN — MEMBERSHIP PLANS
     ═══════════════════════════════════════ */
  const renderMembershipPlans = () => {
    const plans = [
      { name: 'Monthly Kids', price: '45.000', dur: '1 Month', sessWk: '4x/week', trial: '7 days', students: 54, renewal: 'Auto', status: 'Active', color: '#3182CE' },
      { name: 'Monthly Adult', price: '55.000', dur: '1 Month', sessWk: '4x/week', trial: '7 days', students: 48, renewal: 'Auto', status: 'Active', color: COLORS.primary },
      { name: 'Quarterly Adult', price: '120.000', dur: '3 Months', sessWk: '4x/week', trial: 'None', students: 22, renewal: 'Manual', status: 'Active', color: COLORS.warning },
      { name: 'Yearly Premium', price: '400.000', dur: '12 Months', sessWk: 'Unlimited', trial: 'None', students: 14, renewal: 'Manual', status: 'Active', color: COLORS.success },
      { name: 'Trial (7 Days)', price: '0.000', dur: '7 Days', sessWk: '2x/week', trial: '—', students: 4, renewal: 'None', status: 'Active', color: COLORS.textLight },
    ];
    const freezeRequests = [
      { name: 'Fahad Al-Dosari', plan: 'Monthly Kids', reason: 'Travel', dates: 'Feb 20 – Mar 5', status: 'Pending' },
      { name: 'Ali Mansoor', plan: 'Monthly Adult', reason: 'Medical', dates: 'Feb 22 – Mar 1', status: 'Pending' },
      { name: 'Sara Ahmed', plan: 'Monthly Adult', reason: 'Personal', dates: 'Mar 1 – Mar 10', status: 'Approved' },
    ];
    return (
      <>
        <PageHeader title="Membership Plans" subtitle="Manage plans, pricing, and student memberships"
          actions={<button style={btn()}>+ New Plan</button>}
        />

        {/* KPI row */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
          {[
            { label: 'Active Plans', value: '5', icon: '📋', color: COLORS.primary },
            { label: 'Active Members', value: '142', icon: '🎓', color: '#3182CE' },
            { label: 'Monthly Revenue', value: '4,820 BHD', icon: '💰', color: COLORS.success },
            { label: 'Pending Freezes', value: '2', icon: '❄️', color: COLORS.warning },
          ].map((k, i) => (
            <div key={i} style={kpiCard()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: COLORS.textLight, fontFamily: FONTS.body }}>{k.label}</span>
                <span style={{ fontSize: 20 }}>{k.icon}</span>
              </div>
              <div style={{ fontFamily: FONTS.number, fontSize: 26, fontWeight: 800, color: k.color }}>{k.value}</div>
            </div>
          ))}
        </div>

        {/* Plans grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
          {plans.map((p, i) => (
            <div key={i} style={{ ...cardWrap, borderTop: `4px solid ${p.color}`, position: 'relative' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div style={{ fontFamily: FONTS.heading, fontSize: 16, fontWeight: 700, color: COLORS.text }}>{p.name}</div>
                <span style={badge(COLORS.success)}>{p.status}</span>
              </div>
              <div style={{ fontFamily: FONTS.number, fontSize: 28, fontWeight: 800, color: p.color, marginBottom: 14 }}>{p.price} <span style={{ fontSize: 13, fontWeight: 500, color: COLORS.textLight }}>BHD</span></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 12, fontFamily: FONTS.body, marginBottom: 16 }}>
                {[['Duration', p.dur], ['Sessions/wk', p.sessWk], ['Trial', p.trial], ['Renewal', p.renewal]].map(([k, v]) => (
                  <div key={k}><div style={{ color: COLORS.textLight, marginBottom: 2 }}>{k}</div><div style={{ fontWeight: 600, color: COLORS.text }}>{v}</div></div>
                ))}
              </div>
              <div style={{ background: COLORS.bg, borderRadius: 8, padding: '8px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <span style={{ fontFamily: FONTS.body, fontSize: 12, color: COLORS.textLight }}>Active Students</span>
                <span style={{ fontFamily: FONTS.number, fontSize: 18, fontWeight: 700, color: COLORS.text }}>{p.students}</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={{ ...btn(COLORS.bg, COLORS.text), flex: 1, fontSize: 12 }}>Edit</button>
                <button style={{ ...btn('#FFF5F5', COLORS.error), flex: 1, fontSize: 12, border: `1px solid ${COLORS.error}` }}>Archive</button>
              </div>
            </div>
          ))}
        </div>

        {/* Freeze / Pause Requests */}
        <div style={cardWrap}>
          <h3 style={sectionTitle}>Freeze / Pause Requests</h3>
          <table style={tableStyle}>
            <thead><tr>
              <th style={th}>Student</th><th style={th}>Plan</th><th style={th}>Reason</th>
              <th style={th}>Dates</th><th style={th}>Status</th><th style={th}>Action</th>
            </tr></thead>
            <tbody>
              {freezeRequests.map((r, i) => (
                <tr key={i}>
                  <td style={{ ...td, fontWeight: 600 }}>{r.name}</td>
                  <td style={td}>{r.plan}</td>
                  <td style={td}>{r.reason}</td>
                  <td style={{ ...td, fontFamily: FONTS.number, fontSize: 12 }}>{r.dates}</td>
                  <td style={td}><span style={badge(r.status === 'Approved' ? COLORS.success : COLORS.warning)}>{r.status}</span></td>
                  <td style={td}>
                    {r.status === 'Pending' && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button style={{ ...btn(COLORS.success), padding: '4px 12px', fontSize: 11 }}>Approve</button>
                        <button style={{ ...btn(COLORS.error), padding: '4px 12px', fontSize: 11 }}>Reject</button>
                      </div>
                    )}
                    {r.status === 'Approved' && <span style={{ fontSize: 12, color: COLORS.success }}>✓ Frozen</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  /* ═══════════════════════════════════════
     SCREEN — BELT EXAM MANAGEMENT
     ═══════════════════════════════════════ */
  const renderBeltExams = () => {
    const eligibleStudents = [
      { name: 'Fahad Al-Dosari', disc: 'Karate', current: 'Green', next: 'Blue', hours: 200, target: 200, color: '#48BB78', nextColor: '#4299E1' },
      { name: 'Omar Hassan', disc: 'BJJ', current: 'Purple', next: 'Brown', hours: 298, target: 300, color: '#805AD5', nextColor: '#A0522D' },
      { name: 'Layla Khalifa', disc: 'Karate', current: 'Yellow', next: 'Orange', hours: 52, target: 50, color: '#F6E05E', nextColor: '#ED8936' },
      { name: 'Sara Ahmed', disc: 'Boxing', current: 'Blue', next: 'Red', hours: 105, target: 100, color: '#4299E1', nextColor: COLORS.primary },
    ];
    const upcomingExams = [
      { date: 'Feb 28', session: 'Karate Grading', students: 8, belt: 'Green → Blue', status: 'Confirmed', examiner: 'Yousif Salman' },
      { date: 'Mar 5', session: 'BJJ Promotion', students: 4, belt: 'Blue → Purple', status: 'Confirmed', examiner: 'Mahmoud Rashid' },
      { date: 'Mar 12', session: 'Boxing Assessment', students: 6, belt: 'Various', status: 'Tentative', examiner: 'Ahmad Khalil' },
    ];
    const pastExams = [
      { student: 'Noor Khalid', date: 'Feb 10', belt: 'White → Yellow', result: 'Pass', disc: 'Karate' },
      { student: 'Ali Hassan', date: 'Feb 10', belt: 'White → Yellow', result: 'Pass', disc: 'Karate' },
      { student: 'Hassan Ibrahim', date: 'Feb 5', belt: 'Blue → Purple', result: 'Fail', disc: 'BJJ' },
      { student: 'Maryam Jaber', date: 'Feb 5', belt: 'Blue → Purple', result: 'Pass', disc: 'BJJ' },
      { student: 'Khalid Tariq', date: 'Jan 28', belt: 'Yellow → Orange', result: 'Pass', disc: 'Karate' },
    ];
    return (
      <>
        <PageHeader title="Belt Exam Management" subtitle="Schedule exams, track eligibility, record promotions"
          actions={<button style={btn()}>+ Schedule Exam</button>}
        />
        {/* KPI row */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' }}>
          {[
            { label: 'Exam-Ready Students', value: '18', icon: '🏆', color: COLORS.accent },
            { label: 'Exams Scheduled', value: '3', icon: '📅', color: '#3182CE' },
            { label: 'Completed This Month', value: '12', icon: '✅', color: COLORS.success },
            { label: 'Pass Rate', value: '94%', icon: '⭐', color: COLORS.success },
          ].map((k, i) => (
            <div key={i} style={kpiCard()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: COLORS.textLight, fontFamily: FONTS.body }}>{k.label}</span>
                <span style={{ fontSize: 20 }}>{k.icon}</span>
              </div>
              <div style={{ fontFamily: FONTS.number, fontSize: 28, fontWeight: 800, color: k.color }}>{k.value}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 20, marginBottom: 24 }}>
          {/* Exam-Ready Students */}
          <div style={{ ...cardWrap, flex: 2 }}>
            <h3 style={sectionTitle}>Exam-Ready Students</h3>
            <p style={{ fontSize: 12, color: COLORS.textLight, fontFamily: FONTS.body, marginTop: -8, marginBottom: 16 }}>Students who have reached their hours threshold and are eligible for promotion</p>
            {eligibleStudents.map((s, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '14px 0', borderBottom: i < eligibleStudents.length - 1 ? `1px solid ${COLORS.border}` : 'none' }}>
                <div style={{ textAlign: 'center', minWidth: 90 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
                    <div style={{ width: 18, height: 18, borderRadius: '50%', background: s.color }} />
                    <span style={{ fontSize: 11, color: COLORS.textLight }}>→</span>
                    <div style={{ width: 18, height: 18, borderRadius: '50%', background: s.nextColor }} />
                  </div>
                  <div style={{ fontSize: 10, color: COLORS.textLight, fontFamily: FONTS.body, marginTop: 4 }}>{s.current} → {s.next}</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontFamily: FONTS.body, fontSize: 14, color: COLORS.text, marginBottom: 4 }}>{s.name}</div>
                  <div style={{ fontSize: 12, color: COLORS.textLight, fontFamily: FONTS.body, marginBottom: 6 }}>{s.disc}</div>
                  <div style={{ height: 6, background: COLORS.border, borderRadius: 3 }}>
                    <div style={{ width: `${Math.min((s.hours / s.target) * 100, 100)}%`, height: '100%', background: COLORS.success, borderRadius: 3 }} />
                  </div>
                  <div style={{ fontSize: 10, color: COLORS.textLight, fontFamily: FONTS.number, marginTop: 3 }}>{s.hours}/{s.target} hrs</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button style={{ ...btn(), fontSize: 11, padding: '6px 14px' }}>Schedule Exam</button>
                  <button style={{ ...btn(COLORS.bg, COLORS.textLight), fontSize: 11, padding: '6px 10px' }}>Notify Coach</button>
                </div>
              </div>
            ))}
          </div>

          {/* Upcoming Exams */}
          <div style={{ ...cardWrap, flex: 1 }}>
            <h3 style={sectionTitle}>Upcoming Exams</h3>
            {upcomingExams.map((e, i) => (
              <div key={i} style={{ padding: '12px 0', borderBottom: i < upcomingExams.length - 1 ? `1px solid ${COLORS.border}` : 'none' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                  <span style={{ fontFamily: FONTS.number, fontWeight: 700, fontSize: 13, color: COLORS.primary }}>{e.date}</span>
                  <span style={badge(e.status === 'Confirmed' ? COLORS.success : COLORS.warning, '#fff')}>{e.status}</span>
                </div>
                <div style={{ fontFamily: FONTS.body, fontWeight: 600, fontSize: 14, color: COLORS.text, marginBottom: 4 }}>{e.session}</div>
                <div style={{ fontSize: 12, color: COLORS.textLight, fontFamily: FONTS.body }}>{e.belt} &middot; {e.students} students &middot; {e.examiner}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Exam Results */}
        <div style={cardWrap}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <h3 style={{ ...sectionTitle, margin: 0 }}>Recent Exam Results</h3>
            <button style={{ ...btn(COLORS.bg, COLORS.text), fontSize: 12 }}>Export Results</button>
          </div>
          <table style={tableStyle}>
            <thead><tr>
              <th style={th}>Student</th><th style={th}>Discipline</th><th style={th}>Date</th>
              <th style={th}>Belt Level</th><th style={th}>Result</th><th style={th}>Action</th>
            </tr></thead>
            <tbody>
              {pastExams.map((e, i) => (
                <tr key={i}>
                  <td style={{ ...td, fontWeight: 600 }}>{e.student}</td>
                  <td style={td}>{e.disc}</td>
                  <td style={{ ...td, fontFamily: FONTS.number }}>{e.date}</td>
                  <td style={td}>{e.belt}</td>
                  <td style={td}><span style={badge(e.result === 'Pass' ? COLORS.success : COLORS.error)}>{e.result}</span></td>
                  <td style={td}>
                    {e.result === 'Pass'
                      ? <button style={{ ...btn(COLORS.success), fontSize: 11, padding: '4px 12px' }}>Promote Student</button>
                      : <button style={{ ...btn(COLORS.warning), fontSize: 11, padding: '4px 12px' }}>Reschedule</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  /* ═══════════════════════════════════════
     SCREEN — COACH LEAVE REQUESTS
     ═══════════════════════════════════════ */
  const renderLeaveRequests = () => {
    const requests = [
      { coach: 'Ali Abdullah', avatar: 'AA', dates: 'Feb 20 – Feb 22', days: 3, reason: 'Medical', sessions: ['Kickboxing (7 PM)'], status: 'Pending', submitted: '2 hours ago' },
      { coach: 'Ahmad Khalil', avatar: 'AK', dates: 'Feb 25', days: 1, reason: 'Personal', sessions: ['Adult Boxing (5 PM)'], status: 'Pending', submitted: '1 day ago' },
      { coach: 'Mahmoud Rashid', avatar: 'MR', dates: 'Mar 3 – Mar 5', days: 3, reason: 'Travel', sessions: ['BJJ Advanced (7 PM)', 'BJJ Beginner (5 PM)'], status: 'Approved', submitted: '3 days ago' },
      { coach: 'Yousif Salman', avatar: 'YS', dates: 'Feb 14', days: 1, reason: 'Personal', sessions: ['Kids Karate (8 AM)'], status: 'Rejected', submitted: '5 days ago' },
    ];
    const statusColor = { Pending: COLORS.warning, Approved: COLORS.success, Rejected: COLORS.error };
    return (
      <>
        <PageHeader title="Coach Leave Requests" subtitle="Review and approve coach leave & availability changes" />
        {/* KPI row */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'Pending Approval', value: '2', icon: '⏳', color: COLORS.warning },
            { label: 'Approved This Month', value: '5', icon: '✅', color: COLORS.success },
            { label: 'Sessions Affected', value: '8', icon: '⏱️', color: COLORS.primary },
            { label: 'Subs Needed Today', value: '1', icon: '🚨', color: COLORS.error },
          ].map((k, i) => (
            <div key={i} style={kpiCard()}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <span style={{ fontSize: 12, color: COLORS.textLight, fontFamily: FONTS.body }}>{k.label}</span>
                <span style={{ fontSize: 20 }}>{k.icon}</span>
              </div>
              <div style={{ fontFamily: FONTS.number, fontSize: 28, fontWeight: 800, color: k.color }}>{k.value}</div>
            </div>
          ))}
        </div>

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: 0, marginBottom: 20, borderRadius: 8, overflow: 'hidden', border: `1px solid ${COLORS.border}`, width: 'fit-content' }}>
          {['All (4)', 'Pending (2)', 'Approved', 'Rejected'].map((t, i) => (
            <div key={t} style={{ padding: '10px 22px', fontSize: 13, fontWeight: i === 0 ? 700 : 400, fontFamily: FONTS.body, cursor: 'pointer', background: i === 0 ? COLORS.primary : COLORS.bg, color: i === 0 ? '#fff' : COLORS.textLight, borderRight: i < 3 ? `1px solid ${COLORS.border}` : 'none' }}>{t}</div>
          ))}
        </div>

        {/* Request cards */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {requests.map((r, i) => (
            <div key={i} style={{ ...cardWrap, borderLeft: `5px solid ${statusColor[r.status]}`, display: 'flex', gap: 20, alignItems: 'flex-start' }}>
              <div style={{ width: 52, height: 52, borderRadius: '50%', background: COLORS.secondary, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16, fontFamily: FONTS.heading, flexShrink: 0 }}>{r.avatar}</div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div style={{ fontFamily: FONTS.heading, fontSize: 16, fontWeight: 700, color: COLORS.text }}>{r.coach}</div>
                  <span style={badge(statusColor[r.status])}>{r.status}</span>
                </div>
                <div style={{ display: 'flex', gap: 24, marginBottom: 8, fontSize: 13, fontFamily: FONTS.body, color: COLORS.text }}>
                  <span>📅 {r.dates} ({r.days} day{r.days > 1 ? 's' : ''})</span>
                  <span>📝 {r.reason}</span>
                  <span style={{ color: COLORS.textLight, fontSize: 11 }}>Submitted: {r.submitted}</span>
                </div>
                <div style={{ fontSize: 12, fontFamily: FONTS.body, color: COLORS.textLight }}>
                  Sessions affected: {r.sessions.map((s, j) => <span key={j} style={badge(COLORS.bg, COLORS.text)}>{s}</span>).reduce((a, b) => [a, ' ', b])}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0, marginTop: 4 }}>
                {r.status === 'Pending' && (
                  <>
                    <button style={{ ...btn(COLORS.success), fontSize: 12, padding: '8px 16px' }}>✓ Approve</button>
                    <button style={{ ...btn(COLORS.error), fontSize: 12, padding: '8px 16px' }}>✕ Reject</button>
                  </>
                )}
                {r.status === 'Approved' && (
                  <button style={{ ...btn('#3182CE'), fontSize: 12 }} onClick={() => setCurrentScreen('substitute')}>Assign Substitute</button>
                )}
                {r.status === 'Rejected' && (
                  <span style={{ fontSize: 12, color: COLORS.error, fontFamily: FONTS.body, padding: '8px 0' }}>✕ Rejected</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </>
    );
  };

  /* ═══════════════════════════════════════
     SCREEN ROUTER
     ═══════════════════════════════════════ */
  const renderScreen = () => {
    switch (currentScreen) {
      case 'dashboard': return renderDashboard();
      case 'students': return renderStudents();
      case 'student-profile': return renderStudentProfile();
      case 'coaches': return renderCoaches();
      case 'coach-detail': return renderCoachDetail();
      case 'add-coach': return renderAddCoach();
      case 'belt-exams': return renderBeltExams();
      case 'leave-requests': return renderLeaveRequests();
      case 'plans': return renderMembershipPlans();
      case 'admin-login': return renderAdminLogin();
      case 'commission': return renderCommission();
      case 'schedule': return renderSchedule();
      case 'daily-sessions': return renderDailySessions();
      case 'substitute': return renderSubstitute();
      case 'exercises': return renderExercises();
      case 'payments': return renderPayments();
      case 'finance': return renderFinance();
      case 'whatsapp': return renderWhatsApp();
      case 'settings': return renderSettings();
      case 'add-student': return renderAddStudent();
      case 'attendance': return renderAttendance();
      case 'reports': return renderReports();
      case 'session-kiosk': return renderSessionKiosk();
      default: return renderDashboard();
    }
  };

  const isKiosk = currentScreen === 'session-kiosk';
  const isLoginScreen = currentScreen === 'admin-login';
  const effectiveView = isKiosk ? 'tablet' : viewMode;

  return (
    <div style={{ padding: 32, background: '#E2E8F0', minHeight: '100vh', fontFamily: FONTS.body }}>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Montserrat:wght@400;600;700;800&family=Poppins:wght@400;600;700;800&display=swap" rel="stylesheet" />

      {/* View Mode Toggle */}
      <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16, gap: 4 }}>
        {['desktop', 'tablet'].map(mode => (
          <button key={mode} onClick={() => setViewMode(mode)}
            style={{
              padding: '8px 24px', borderRadius: 8, border: 'none', cursor: 'pointer',
              fontFamily: FONTS.body, fontWeight: 600, fontSize: 13,
              background: effectiveView === mode ? COLORS.primary : '#fff',
              color: effectiveView === mode ? '#fff' : COLORS.text,
              boxShadow: '0 1px 3px rgba(0,0,0,.08)',
              textTransform: 'capitalize',
            }}>
            {mode === 'desktop' ? '🖥️ Desktop' : '📱 Tablet'}
          </button>
        ))}
        <button onClick={() => setCurrentScreen('admin-login')}
          style={{
            padding: '8px 24px', borderRadius: 8, border: 'none', cursor: 'pointer',
            fontFamily: FONTS.body, fontWeight: 600, fontSize: 13,
            background: isLoginScreen ? COLORS.primary : '#fff',
            color: isLoginScreen ? '#fff' : COLORS.text,
            boxShadow: '0 1px 3px rgba(0,0,0,.08)',
          }}>
          🔐 Login
        </button>
      </div>

      {isLoginScreen ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: 20 }}>
          {renderScreen()}
        </div>
      ) : effectiveView === 'tablet' ? (
        <TabletFrame noSidebar={isKiosk}>
          {renderScreen()}
        </TabletFrame>
      ) : (
        <BrowserFrame>
          {renderScreen()}
        </BrowserFrame>
      )}
    </div>
  );
}
